/*
 * A conversation, as state — no DOM, no markup, no styling.
 *
 * THIS IS THE SEAM. The panel that renders a conversation is going to be redesigned, probably more
 * than once, and none of that should be able to break how a turn runs. So everything that decides
 * something lives here — what the messages are, when a turn is in flight, how a stream lands, what
 * an error says — and the component is a pure function of `messages` + `status`.
 *
 * Replacing the entire panel means reading two properties and calling one method. Nothing about the
 * markup is load-bearing.
 *
 * Manager-pattern, like every other piece of state in the app: it holds the state, mutates it, and
 * emits `change`. Components subscribe; they never fetch.
 */
import { routeFor } from '@vitesse/agent-loop';
import { returnProse, personStoppedBelow, uncitedSources, misattributed, TURN_RUN_PREFIX } from '@vitesse/agent-loop';
import { RunTree, latestAttempt } from './run-tree';
import { attachmentProse, inlineable } from './transcript';
import { referencesOf, withReferenceProse } from './references';
import { cancelAsk } from './ask-user';
import { driveToCompletion } from './driver';
import { setRunPartial } from './run-partials';
import { gateToExpert, makeBrowserStep, outsideSubset, prewarmForExpert, scopeToExpert } from './step';
import { effectiveSpecialist } from './specialists';
import { textOf, toolsOf } from './manager';
import { TEXT_MODELS } from './model-catalog';
/**
 * One tool call inside a turn, and how it went.
 *
 * TWO PHASES, mirroring the durable record: an entry appears as `running` BEFORE dispatch and is
 * resolved after. That ordering is the same discipline the archive uses, and for the same reason —
 * a tab that dies mid-tool leaves a row saying "this was attempted", which is the honest reading.
 * A row that only appeared on completion would make a crashed write indistinguishable from one that
 * never happened.
 */
/**
 * Paragraphs into one text: each trimmed at its end before the next begins, EXCEPT the last, which
 * may still be arriving and must read exactly as it has so far — a stream that ends on a space is
 * mid-word, not finished.
 */
export function joinProse(paragraphs) {
    return paragraphs
        .map((t, i) => (i === paragraphs.length - 1 ? t : t.trimEnd()))
        .filter(Boolean)
        .join('\n\n');
}
/** The turn's sequence, created on first use. */
export function turnParts(turn) {
    turn.parts ??= [];
    return turn.parts;
}
/**
 * Keep the aggregates true to the sequence. `text` is every text part in order; `reasoning` every
 * thinking part in order; `reasoningMs` their sum. Called after every change to `parts`, so nothing
 * reading the whole turn ever sees it disagree with the order it was drawn in.
 */
export function syncAggregates(turn) {
    const parts = turn.parts ?? [];
    // Each paragraph trimmed at its end before joining: a round that ended on a space would
    // otherwise put a stray blank before the next round's first word.
    turn.text = joinProse(parts.filter((p) => p.kind === 'text').map((p) => p.text));
    const thought = parts.filter((p) => p.kind === 'reasoning');
    if (thought.length) {
        turn.reasoning = thought.map((p) => p.text).filter(Boolean).join('\n\n');
        const ms = thought.reduce((a, p) => a + (p.ms ?? 0), 0);
        if (ms)
            turn.reasoningMs = ms;
    }
}
/**
 * The archive's rows in the form the model reads.
 *
 * The archive keeps a step as AI-SDK parts: an assistant row carrying `tool-<name>` parts with the
 * input (the call), then a tool row carrying the same part with its output (the result). On the
 * wire those are an assistant message with `toolCalls` and a `tool` message per result, keyed by
 * call id. A result whose call is not in the window — the page started after it — is dropped:
 * a provider rejects an answer to a question it cannot see. Reasoning stays out, as `append` keeps
 * it out: a model's scratchpad is not something it said.
 */
export function archiveToWire(rows) {
    const out = [];
    const issued = new Set();
    for (const m of rows) {
        const text = textOf(m.content);
        if (m.role === 'user') {
            // With its references, as the model read it when the message was sent.
            if (text)
                out.push({ role: 'user', content: withReferenceProse(text, referencesOf(m.content)) });
            continue;
        }
        const tools = toolsOf(m.content);
        if (m.role === 'tool') {
            for (const t of tools) {
                if (!issued.has(t.id))
                    continue;
                const content = t.data === undefined ? (t.message ?? '') : `${t.message ?? ''}\n\n${JSON.stringify(t.data)}`;
                out.push({ role: 'tool', content, toolCallId: t.id });
            }
            continue;
        }
        // Assistant. A row that only records calls has no prose; a row with neither is nothing.
        const calls = tools.filter((t) => !issued.has(t.id));
        for (const c of calls)
            issued.add(c.id);
        if (!text && !calls.length)
            continue;
        out.push({
            role: 'assistant',
            content: text,
            ...(calls.length ? { toolCalls: calls.map((c) => ({ id: c.id, name: c.name, argumentsJson: c.argumentsJson })) } : {}),
        });
    }
    return out;
}
export function durableTranscriptStore(writer) {
    const mirror = memoryTranscriptStore();
    // The tool NAME per call id, remembered from the dispatch we just wrote.
    //
    // The driver's `appendToolResult` carries only the id — it has no reason to know the name, and
    // widening its interface to carry one would push a storage detail into every implementation. The
    // part type on the wire is `tool-<name>`, so the store recovers it from the phase it recorded
    // moments earlier. Bounded by the calls in flight, and cleared as each resolves.
    const pendingToolNames = new Map();
    /*
     * THE TEXT OF EACH ATTACHED TEXT FILE, by sha — what the prompt mirror inlines.
     *
     * Filled at upload (the bytes are in hand) and on resume (read back from the store), so a CSV
     * attached an hour ago is still in the prompt an hour later, and after a reload. Bounded by the
     * attachments of the threads this tab has opened; a picture or a PDF never enters it.
     */
    const inlineText = new Map();
    /** What the model reads for a message: its text, then its attachments as prose. */
    const withAttachments = (text, files) => files?.length ? `${text}\n\n${attachmentProse(files, inlineText)}` : text;
    const learnText = async (id, files) => {
        for (const f of files) {
            if (inlineText.has(f.sha256) || !inlineable(f.mediaType) || !writer.readFile)
                continue;
            const bytes = await writer.readFile(id, f.sha256);
            if (bytes)
                inlineText.set(f.sha256, new TextDecoder().decode(bytes));
        }
    };
    /*
     * PICTURES GO TO THE MODEL AS PICTURES. An attached PNG used to reach the prompt as its
     * filename, so every model — seeing or not — answered that it could not inspect the pixels.
     * The bytes are read back once per attachment and carried base64 beside the text; the chat call
     * decides per model whether to send them or say they cannot be seen.
     */
    const imageData = new Map();
    const imagesOf = async (id, files) => {
        const out = [];
        for (const f of files ?? []) {
            if (!isImageType(f.mediaType) || f.size > IMAGE_PROMPT_MAX_BYTES)
                continue;
            let img = imageData.get(f.sha256);
            if (!img && writer.readFile) {
                const bytes = await writer.readFile(id, f.sha256);
                if (bytes) {
                    img = { mediaType: f.mediaType, base64: base64Of(bytes) };
                    imageData.set(f.sha256, img);
                }
            }
            if (img)
                out.push(img);
            if (out.length >= IMAGES_PER_MESSAGE)
                break;
        }
        return out.length ? out : undefined;
    };
    /*
     * AN ADOPTED RUN REMEMBERS WHAT IT DID.
     *
     * The mirror is filled by this tab's own writes and by `prime` when a thread is opened on
     * screen. A run this tab ADOPTS — after a reload, or picked up from the pool because nobody was
     * driving it — is neither: nothing had put its past in the mirror, so every step was built from
     * the task alone and the model started over each time. Observed, verbatim, step after step:
     * "Let me start by reading the model" — the same read, the same list, until the step cap. It
     * looked like a model that could not think; it was a driver that had thrown its memory away.
     *
     * So an empty mirror asks the archive first, once, and keeps what it gets. In wire form: an
     * assistant turn with the calls it issued, a tool row per outcome with the id it answers — the
     * shape a provider recognises. Prose alone would leave the calls unrecognised and the model
     * repeating them, which is the failure the memory store's comment already describes.
     */
    const historyOf = async (id) => {
        const mine = await mirror.history(id);
        if (mine.length || !writer.readHistory)
            return mine;
        const rows = await writer.readHistory(id);
        if (!rows?.length)
            return mine;
        const wire = archiveToWire(rows);
        if (wire.length)
            await mirror.restore?.(id, wire);
        return wire;
    };
    return {
        history: historyOf,
        // Mirror only — see TranscriptStore.prime. The archive already holds these.
        async prime(id, turns) {
            // Attached text is read back before the mirror is filled, so the FIRST prompt after a resume
            // already carries it — filling first and patching later would race the person's next message.
            for (const x of turns)
                if (x.files?.length)
                    await learnText(id, x.files);
            const primed = [];
            for (const x of turns)
                primed.push({ role: x.role, text: withReferenceProse(withAttachments(x.text, x.files), x.references), images: await imagesOf(id, x.files) });
            await mirror.prime?.(id, primed);
        },
        ...(writer.upload
            ? {
                async upload(id, file) {
                    const ref = await writer.upload(id, file);
                    // Read here, from the File in hand, rather than back from the store: the bytes are on
                    // this side of the wire already, and a text file the person just picked is exactly the
                    // thing the next prompt has to carry.
                    if (ref && inlineable(ref.mediaType))
                        inlineText.set(ref.sha256, await file.text());
                    if (ref && isImageType(ref.mediaType) && file.size <= IMAGE_PROMPT_MAX_BYTES)
                        imageData.set(ref.sha256, { mediaType: ref.mediaType, base64: base64Of(new Uint8Array(await file.arrayBuffer())) });
                    return ref;
                },
            }
            : {}),
        // Mirror only too — see TranscriptStore.retract. The archive keeps what was seen.
        async retract(id) {
            await mirror.retract?.(id);
        },
        // And this — see TranscriptStore.stamp. The archive keeps what the person wrote. Through
        // `historyOf` first: an edited question is asked the moment its branch opens, before the thread
        // has been read, and a line put in an empty mirror would land on nothing.
        async stamp(id, line) {
            await historyOf(id);
            await mirror.stamp?.(id, line);
        },
        async append(id, role, text, reasoning, reasoningMs, telemetry, files, _images, references, settings) {
            const r = await writer.appendMessage(id, role, text, reasoning, reasoningMs, telemetry, files, references, settings);
            if (!r.ok)
                throw new Error(r.message);
            // Prose only into the MIRROR, deliberately. The mirror is what the next turn's prompt is
            // built from, and a model's own scratchpad is not something it said — feeding thinking back
            // as conversation would have it answering its own half-formed drafts. The archive keeps it
            // because a person reading back wants to see the work; the prompt does not.
            // Attachments DO go in, as prose: the person attached them to be read. So do references —
            // the ids the person picked, which are the reason a mention exists.
            await mirror.append(id, role, withReferenceProse(withAttachments(text, files), references), undefined, undefined, undefined, undefined, await imagesOf(id, files));
            return r.seq;
        },
        /** A finished agent's answer. Written through with its author and a stable id; mirrored as a
         *  plain user-slot message, because that is what the model reads it as. */
        ...(writer.appendReturn
            ? {
                async appendReturn(id, text, fromRunId) {
                    const r = await writer.appendReturn(id, text, fromRunId);
                    if (!r.ok)
                        throw new Error(r.message);
                    await mirror.append(id, 'user', text);
                    return r.seq;
                },
            }
            : {}),
        // Present only when the writer can do BOTH phases: a driver checks for the pair before it will
        // dispatch, and offering one without the other would let a call be recorded with no way to
        // resolve it — the exact state the two-phase rule exists to make impossible.
        ...(writer.appendToolCalls && writer.appendToolResult
            ? {
                /*
                 * WRITE THROUGH **AND** MIRROR — both, exactly as `append` does above.
                 *
                 * These wrote only to the archive. `history()` reads the MIRROR, so the next request in a
                 * tool loop was assembled without the assistant turn that issued the call and without the
                 * result that answered it: the model asked its question again, got the same nothing, and
                 * called the same tool until the round cap stopped it. Eight identical calls, observed.
                 *
                 * The archive was perfectly correct throughout, which is what made it invisible — the
                 * transcript a human reads had the whole exchange, and only the prompt was missing it.
                 */
                async appendToolCalls(id, calls, reasoning, reasoningMs) {
                    const r = await writer.appendToolCalls(id, calls, reasoning, reasoningMs);
                    if (!r.ok)
                        throw new Error(r.message);
                    for (const c of calls)
                        pendingToolNames.set(c.id, c.name);
                    await mirror.appendToolCalls?.(id, calls);
                },
                async appendToolResult(id, callId, outcome) {
                    const name = pendingToolNames.get(callId) ?? 'tool';
                    const r = await writer.appendToolResult(id, callId, name, outcome);
                    if (!r.ok)
                        throw new Error(r.message);
                    pendingToolNames.delete(callId);
                    await mirror.appendToolResult?.(id, callId, outcome);
                    // What it parked, so the live turn can draw it. The mirror is prose-only and has
                    // nothing to say about artifacts — it is what the MODEL reads back.
                    return r.files?.length ? { files: r.files } : undefined;
                },
            }
            : {}),
    };
}
export function memoryTranscriptStore() {
    const byConversation = new Map();
    /* When each turn's message was sent (see TranscriptStore.stamp), BESIDE the message rather than in
       its text, so a retry replaces the line instead of stacking a second one on top. Keyed by the
       message itself: `prime` and `restore` put new messages in, and the lines of the old ones — a
       thread from before a reload, which never had them — go with them. */
    const sentAt = new WeakMap();
    return {
        async prime(id, turns) {
            byConversation.set(id, turns.map((x) => ({ role: x.role, content: withReferenceProse(x.text, x.references), ...(x.images?.length ? { images: x.images } : {}) })));
        },
        async history(id) {
            const list = byConversation.get(id) ?? [];
            return list.map((m) => {
                const line = sentAt.get(m);
                return line ? { ...m, content: `${line}\n${m.content}` } : m;
            });
        },
        async stamp(id, line) {
            const last = [...(byConversation.get(id) ?? [])].reverse().find((m) => m.role === 'user');
            if (last)
                sentAt.set(last, line);
        },
        async restore(id, messages) {
            byConversation.set(id, messages.slice());
        },
        async retract(id) {
            const list = byConversation.get(id) ?? [];
            let keep = list.length;
            while (keep > 0 && list[keep - 1].role !== 'user')
                keep -= 1;
            byConversation.set(id, list.slice(0, keep));
        },
        async append(id, role, text, _reasoning, _reasoningMs, _telemetry, _files, images, references) {
            const list = byConversation.get(id) ?? [];
            list.push({ role, content: withReferenceProse(text, references), ...(images?.length ? { images } : {}) });
            byConversation.set(id, list);
        },
        /** The model reads this store, and to a model a return IS a user-slot message — the authorship
         *  it carries is for the RECORD and its readers, which a mirror does not have. */
        async appendReturn(id, text) {
            const list = byConversation.get(id) ?? [];
            list.push({ role: 'user', content: text });
            byConversation.set(id, list);
        },
        /*
         * BOTH PHASES, even in memory — and in the WIRE FORM, not as narration.
         *
         * A driver refuses to run tools unless the store can record a call before it runs and its
         * outcome after. The two-phase rule is about ordering, not durability, so an in-memory log
         * honours it exactly as well as a remote one does.
         *
         * These wrote English: `[calling x]` and `[c1 → ok: …]`, as assistant turns. That reads fine to
         * a person and means nothing to a model — a provider recognises a tool result only as a `tool`
         * message carrying the id of the call it answers, attached to the assistant turn that made it.
         * Written as prose, the model saw its question answered by a narrator, did not recognise the
         * result, and called the same tool again. Observed: eight identical calls until the round cap.
         */
        async appendToolCalls(id, calls) {
            const list = byConversation.get(id) ?? [];
            // The assistant turn that ISSUED the calls. Content is empty because the model wrote none —
            // it called a tool instead of speaking, which is a complete turn.
            list.push({ role: 'assistant', content: '', toolCalls: calls });
            byConversation.set(id, list);
        },
        async appendToolResult(id, callId, outcome) {
            const list = byConversation.get(id) ?? [];
            // `message` first, always: it is prose the model can act on, and on failure it is the only
            // thing there. `data` follows when the call succeeded and produced something structured.
            const content = outcome.data === undefined ? outcome.message : `${outcome.message}\n\n${JSON.stringify(outcome.data)}`;
            list.push({ role: 'tool', content, toolCallId: callId });
            byConversation.set(id, list);
        },
    };
}
/**
 * Are tools genuinely usable, or only half-wired?
 *
 * ALL FOUR or none. Offering declarations without a dispatcher gives the model capabilities that
 * cannot run; dispatching without the two-phase record produces a tool call the transcript cannot
 * explain, and — because the next turn reads that transcript — a call whose outcome is invisible is
 * indistinguishable from one that never happened. On this platform that difference is a physical
 * duplicate, so a partially-wired setup gets NO tools rather than most of them.
 */
function toolsUsable(o, store) {
    return !!o.tools && !!o.dispatch && !!store.appendToolCalls && !!store.appendToolResult;
}
/**
 * How many times one `send` may go round the tool loop.
 *
 * A model can legitimately chain a few calls — look something up, then act on what it found — and
 * cutting it off at one would make every multi-step answer wrong. It can also loop forever calling
 * the same tool, and each pass costs a metered model call. Eight is well past any real chain and
 * far short of a runaway bill; hitting it is reported to the model as the reason it must answer now,
 * rather than the turn silently ending.
 */
/**
 * What ONE TAB may spend advancing agent trees in a single pass.
 *
 * Distinct from a run's own budget, which is the user's decision and is durable in the run record.
 * This one bounds the blast radius of THIS tab: a driver looping over a tree it just adopted should
 * not be able to spend without limit because a page happened to be open, and it is correctly
 * forgotten when the tab goes away.
 */
export const TAB_DRIVE_BUDGET_USD = 2;
/**
 * THE STEP THIS TAB DRIVES RUNS WITH — one definition, whether the run was started here or found.
 *
 * A conversation builds it to drive the trees it spawns; the manager builds the same one to adopt
 * trees an earlier tab left behind. Two constructions would be two lists of what a step needs, and
 * the second would be the one that forgot the roster or the door.
 */
export function browserStep(opts, store) {
    return makeBrowserStep({
        aiUrl: opts.aiUrl,
        token: opts.token,
        platform: opts.platform,
        // The run's memory IS its conversation, and this store is already the one writing this
        // thread's. A second store beside it would give the same runs two records.
        io: store,
        defaultModel: opts.defaultModel,
        // The SAME union and the SAME door the person's own turns use, so a spawned specialist can
        // actually do the thing it specialises in — and is gated identically while doing it.
        tools: opts.tools,
        dispatch: opts.dispatch,
        // The roster, so a spawned run is held to its expert's declared subset rather than handed
        // whatever this tab happens to have registered.
        experts: opts.experts,
        /*
         * THE AGENT'S ANSWER, ARRIVING.
         *
         * Published to the per-tab registry, because the surface that draws an agent's thread is a
         * different component from the one driving it. A run's durable message does not exist until
         * its model call returns, so without this a viewer had nothing at all to paint for the length
         * of the call — and for an agent that calls no tools, that is the whole run.
         */
        onPartial: setRunPartial,
    });
}
/**
 * When a thread is long enough to have chapters, and how much it must grow before they are re-read.
 *
 * A floor because segmenting a short exchange yields one chapter that restates the title, and a
 * stride because each pass is a billed model call — running it per turn would double the cost of
 * every message to keep a navigation rail current that nobody is looking at yet.
 *
 * BOTH LOWERED (12/10 → 6/4). At the old numbers a thread needed a dozen turns before it had any
 * contents at all and then went ten more before they moved, so the button was missing exactly when
 * a conversation was becoming worth navigating and stale for most of the time after. Six turns is
 * still past the point where one chapter would just restate the title; four is close enough that a
 * notable exchange shows up while it is still the thing you were reading.
 *
 * The cost is real and bounded: one small model call per four turns, on a thread that is already
 * making one billed call per turn.
 */
/*
 * LOWERED TO ONE EXCHANGE (6/4 → 2/2), because "the contents stop before the end" is the complaint.
 *
 * A stride of four turns means the newest exchange can sit unlabelled for three of them, and since
 * a send is two turns that is most of a conversation's live edge. The rail then describes where you
 * have BEEN and not where you are, which reads as it having quietly stopped working. At a stride of
 * two, the contents are never more than the current exchange behind.
 *
 * The floor is now ONE EXCHANGE (2 turns) rather than a "long enough to have structure" threshold.
 * The old argument — that one chapter over a short thread only restates the title — is true and is
 * not worth the cost of the rail being absent exactly when someone is deciding whether it works.
 * A thread has contents from its first answer onward, and every later pass re-segments the whole
 * thing anyway, so an early chapter is a first draft rather than a commitment.
 *
 * The cost stays bounded and is one small model call per two turns on a thread already making a
 * billed call every turn — and each pass REPLACES the generated chapters rather than appending, so
 * running more often re-consolidates the whole shape instead of accreting entries.
 */
export const CHAPTER_FLOOR_TURNS = 2;
export const CHAPTER_STRIDE_TURNS = 2;
/** RunStatus.FINISHED, as the generated enum's numeric value. */
const RUN_FINISHED = 4;
const MAX_TOOL_ROUNDS = 8;
/** Pictures carried to the model per message, and the largest one that is. Bounded because each
 *  rides in the prompt on every later turn of the thread. */
const IMAGES_PER_MESSAGE = 4;
const IMAGE_PROMPT_MAX_BYTES = 6 * 1024 * 1024;
const isImageType = (mediaType) => /^image\/(png|jpe?g|gif|webp)$/i.test(mediaType);
function base64Of(bytes) {
    let bin = '';
    for (let i = 0; i < bytes.length; i += 0x8000)
        bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    return btoa(bin);
}
/** Whether a model can look at pictures — the catalogue's `files` flag; a model the catalogue does
 *  not know is given the benefit of the doubt. */
const seesImages = (model) => TEXT_MODELS.find((m) => m.value === model)?.files !== false;
/** The effort a thread starts at — and how a reopened thread tells that nobody has chosen one yet. */
const DEFAULT_EFFORT = 'medium';
/** The pause before the one retry of a model call that failed upstream. */
const UPSTREAM_RETRY_MS = 1500;
/**
 * HOW LONG A TURN MAY SAY NOTHING AT ALL BEFORE IT IS TREATED AS HUNG.
 *
 * A turn holds the composer: the box is disabled and reads "Thinking…" until it ends. So a turn
 * that never ends is a thread that can never be used again, and one was found sitting that way for
 * 71 minutes with a Stop button and a blinking cursor and nothing behind either. The transport
 * takes no abort signal, so a hung call cannot be cancelled on the wire — what can be ended is the
 * TURN, and that is what gives the thread back.
 *
 * SILENCE, not elapsed time. A turn is legitimately long: a tool round, a reasoning model, a
 * hand-off to an agent. What none of those do is produce nothing — every delta, every tool row and
 * every result passes through `emit`. The longest genuine silence observed on this stack is a
 * single first model call of eight to nine minutes on a flash model at high effort, so fifteen is
 * wide enough to never fire on real work and short enough that a hang is a nuisance rather than the
 * end of a thread.
 */
const TURN_SILENCE_MS = 15 * 60_000;
/** How often the silence above is checked. Coarse on purpose: it is a watchdog, not a stopwatch. */
const TURN_WATCH_MS = 30_000;
/** What the turn says when the watchdog ended it. Names the cause, and says the thread is usable —
 *  a reader who has waited a quarter of an hour needs both. */
const HUNG_TURN_MESSAGE = 'This turn stopped sending anything back and was ended after 15 minutes of silence. Nothing further will arrive from it. Ask again to carry on.';
/** What a tool row says when the person stopped the turn before it answered. */
export const STOPPED_TOOL_MESSAGE = 'Stopped by the person before it finished.';
/**
 * WHEN A TURN'S MESSAGE WAS SENT — the line the model reads at the top of it.
 *
 * THE MODEL HAS NO CLOCK, and nothing told it what time it was. Asked the time in Paris it answered
 * with a date out of its training data — confidently, in full detail, and wrong by six weeks. That
 * is the worst shape a wrong answer takes: it looks researched. The web search could not save it
 * either, because a search result carries no date the model trusts over its own assumption.
 *
 * The browser is the only participant that actually knows, so it says so. On EVERY turn rather than
 * once, because the answer changes while the conversation is open — a thread left running overnight
 * would otherwise still be told it is yesterday.
 *
 * ON THE MESSAGE, NOT IN THE SYSTEM TEXT (see `TranscriptStore.stamp`). It was a system block,
 * rebuilt for every call, and the system text comes BEFORE the history on every provider: prompt
 * caching reads a prefix, so a clock that changed every second left nothing after it cacheable.
 * Measured on a Data Scientist thread: the cached share stayed at 12,032 tokens — the tools and the
 * instructions up to the clock — while the prompt grew from 14k to 34k, the rounds of one tool loop
 * seconds apart included. Written once on the message that starts the turn, it is history like the
 * rest, and every call's prompt begins with the previous one's.
 *
 * It is also the more exact statement. An earlier question carries the time IT was asked, so "the
 * last two hours" in yesterday's message reads as yesterday's two hours, which a single "now" in the
 * system text could not say. To the minute: within a turn the clock is the moment of asking, and a
 * tool loop is not long enough for seconds to be the question.
 */
export function sentAtLine(now) {
    /*
     * THE ZONE, THE OFFSET, AND THE LOCAL DATE — all three, because each answers a question the
     * others cannot.
     *
     * The IANA zone carries the DST rule. The numeric offset is what lets a source stamped in UTC or
     * another zone be converted without the model reasoning about European summer time. And the LOCAL
     * date has to be stated separately because it is not always the UTC date: at 00:30 in Paris it is
     * already tomorrow locally while UTC is still yesterday, so a model given only the UTC time
     * will confidently answer "today" with the wrong day for half an hour every night.
     */
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const offsetMin = -now.getTimezoneOffset(); // getTimezoneOffset is inverted, and already DST-aware
    const sign = offsetMin >= 0 ? '+' : '-';
    const offset = `${sign}${String(Math.floor(Math.abs(offsetMin) / 60)).padStart(2, '0')}:${String(Math.abs(offsetMin) % 60).padStart(2, '0')}`;
    const localDate = now.toLocaleDateString('en-CA'); // en-CA is ISO-shaped: YYYY-MM-DD
    const localTime = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
    const weekday = now.toLocaleDateString('en-GB', { weekday: 'long' });
    const utc = now.toISOString(); // YYYY-MM-DDTHH:MM:SS.sssZ
    return `[Sent ${localDate} ${localTime} local (${weekday}, ${zone}, UTC${offset}) = ${utc.slice(0, 10)} ${utc.slice(11, 16)} UTC]`;
}
/**
 * The standing instructions for a chat turn — the same text on every call, which is what lets a
 * provider cache them. What varies per turn is on the messages: see `sentAtLine`.
 */
export function standingPrompts(experts = []) {
    return [
        {
            content: 'You are Vitesse, an industrial-automation assistant. Answer from evidence you can point at. ' +
                'When you do not know something, say so plainly rather than filling the gap with a plausible guess. ' +
                'Before you create, write, or bind anything, search or list what already exists on that scope first — ' +
                'a controller, a model, a dashboard roster, whatever the task touches. Guessing an id and finding out it ' +
                "was wrong costs a failed call; searching costs one and tells you what's really there.",
            cache: true,
        },
        {
            content: 'WHAT YOU CALL THINGS WHEN YOU WRITE: say RELAY, never "gateway", and say HMI or dashboard as the ' +
                'customer did. `gateway` is the name of a tool PARAMETER and of the ids you pass — it is our API, not our ' +
                'product vocabulary, and a person reading your answer should never see it. This rename is invisible in the ' +
                'tool surface and visible in every sentence you write.\n\n' +
                'Vocabulary a customer uses interchangeably — treat these as the SAME thing, not different concepts to search for: ' +
                'a "relay" is also called a "gateway" or "the box" (one Vitesse Controller). A "dashboard" is also called an "HMI" ' +
                'or "screen". Never search a PLC symbol catalogue for a word that names the SCOPE itself (the relay, the ' +
                'dashboard) — "relay" and "health" are not signal names, and searching for them wastes a call and returns nothing. ' +
                '\n\nThe scope hierarchy, and what lives at each level: a relay HAS controllers (each with its own PLC/CNC ' +
                'signals), HAS models (each with its own statechart inputs and outputs), and HAS dashboards (each with tiles ' +
                'that bind to one signal or one variable). "The relay\'s health" usually means its own connectivity and ' +
                "resource status, not something buried in a controller's signal list — check the relay itself before you " +
                'search inside anything it hosts.\n\n' +
                'Every relay-scoped tool (models.*, symbols.*, dashboards.*, history.*, program.*) takes a `gateway` ' +
                'parameter — pass the id or name from gateways.list/gateways.search directly, for ANY relay the org owns. ' +
                'You do not need it on screen and you do not need to navigate anywhere for your own reads; omit `gateway` ' +
                'only when you mean "whatever is currently open", the same way a person would leave it unsaid mid-conversation ' +
                'about the screen in front of them.\n\n' +
                'IDs are COPIED, never retyped. Once a tool call returns an id (a relay id, a controller id, a symbol path), ' +
                'pass that exact string forward — do not reconstruct it from memory a few calls later. A single mistyped ' +
                'character is a silent wrong-answer, not an error you will notice.\n\n' +
                'The memory tools (ai_admin_get_memory_graph, ai_admin_list_memory) recall what was learned in PAST ' +
                'conversations — use them when a person references something you have no other way to know. They are not a ' +
                'first move: reaching for them before you have even tried the platform tools for the thing actually in front ' +
                'of you (what is on screen, the relay list, the symbol catalogue) wastes a call on the wrong kind of answer.\n\n' +
                'When a person names equipment by what it DOES rather than its raw id ("the conveyor", "le convoyeur", "tank ' +
                'pressure"), one search rarely finds it on the first try — a raw PLC path looks nothing like the English word for ' +
                'it. Before asking the person for an id, try a few searches in the SAME call round rather than one at a time: ' +
                'the term as given, its English translation if it was in another language, common abbreviations (conveyor→conv, ' +
                'temperature→temp, pressure→press), and an obvious synonym (motor→drive, conveyor→belt). Only ask the person ' +
                'once those have genuinely turned up nothing. And once you learn what a symbol actually controls — the person ' +
                'said so, or it was the only sensible match — set its label with symbols.label; that is what makes the next ' +
                "search (yours or a colleague's) find it in one try instead of five.\n\n" +
                'REFERENCES. A message can end with a [Referenced with @ …] block: entities the person picked from a list, ' +
                'each with its kind, its relay (gateway id) and its own id. Those ids are exact — pass them straight to the ' +
                'tools, and do not list or search to find something that is already referenced. A reference describes what a ' +
                'thing IS (its name, ids, inputs, tiles), never its live state, so read current values with the tools. To point ' +
                'at one of them in your answer, copy the link at the end of its line exactly, such as ' +
                '[@Conveyor PLC](ref:controller/…); it shows as a chip that opens the entity. Never write a page path or URL ' +
                'for an entity (/models/…, /dashboards/…, /gateways/…): the panel cannot open those, and a click on one lands ' +
                'on the home page.',
            cache: true,
        },
        {
            /*
             * HOW TO READ THE CLOCK, AND THE INSTRUCTION THAT SEARCH RESULTS ARE NOT ONE.
             *
             * The clock itself is the [Sent …] line on each message (see `sentAtLine`); this says what it
             * is, so a line the person did not type is not taken for something they said, or ignored.
             *
             * The first version of this block gave the date and said to prefer it over "any date you read
             * in a search result". It fixed the date and left the clock: asked the weather at 19:15, the
             * model read "9:23 AM" out of a cached AccuWeather snippet, reported that it was morning, and
             * gave a temperature ten hours stale — while getting the date right, which made the whole
             * answer read as freshly researched.
             *
             * A timestamp inside a page is a claim about when that page was written or crawled. It is
             * evidence of the source's age, never of the current time, and saying so explicitly is the
             * difference between a model that reconciles and one that overwrites its own clock.
             *
             * No cache marker of its own: the roster's, after it, covers it, and a fourth would use up the
             * four Anthropic allows. A surface with no roster leaves these few lines uncached there.
             */
            content: 'Each message from the user starts with a [Sent …] line. The app adds it from the user\'s own machine ' +
                'at the moment the message is sent — the user did not type it, and it is authoritative. The newest one ' +
                'is the current time: use it for anything time-dependent — what day it is, what time of day it is, ' +
                'whether something is past or upcoming, and how old a source is. An earlier message carries the time ' +
                'IT was sent, so "the last two hours" in an earlier message meant the two hours before its own line. ' +
                'Messages from before the page was reloaded may have no line. ' +
                'Near midnight the local date and the UTC date differ — use the local one for "today"; the offset in ' +
                'the line already accounts for daylight saving. Never write a [Sent …] line in your answers. ' +
                'Timestamps inside search results and web pages are NOT the current time — they record when ' +
                'that page was written or last crawled and are routinely hours or days stale. A page reading ' +
                '"9:23 AM" is not evidence that it is now morning. Reconcile every such timestamp against the newest ' +
                '[Sent …] line, prefer sources you can date, and if you cannot establish how fresh something is, ' +
                'say so rather than presenting it as current.',
        },
        /*
         * THE ROSTER, BY NAME — otherwise a specialist is unreachable in practice.
         *
         * `spawn_subagent` takes an `expert_id` and its description says to leave it empty "unless you
         * have a specific reason", which is right: naming a slot at random is worse than the default.
         * But nothing told the model which slots EXIST, so there was never a specific reason available
         * and a seeded expert would sit unspawned forever — working perfectly, and never called.
         *
         * IDS ONLY. That is all this side has (the roster the handshake sends carries ids, a model and
         * a step cap — never the prompt), and it is enough: a slot is named for the job it does, and
         * once spawned the expert's own instructions arrive at the edge. Cached with the stable half:
         * a roster changes when an admin edits it, not per turn.
         */
        ...(experts.length
            ? [
                {
                    /*
                     * WHAT EACH ONE IS FOR, not just what it is called.
                     *
                     * This was a bare list of ids with "prefer one when the task matches what its NAME
                     * describes" — so `plc-program-analyst` had to carry the whole decision, and a
                     * specialist an org named after their own machine carried none of it.
                     *
                     * Their TOOLS are deliberately still absent. A caller needs the job, not the
                     * procedures it takes: tool lists change on a role edit and would leave every peer's
                     * prompt describing a reach that has moved, while teaching the caller nothing it can
                     * act on.
                     */
                    content: 'Specialists you can delegate to with spawn_subagent:\n' +
                        experts
                            .map((e) => {
                            const name = e.displayName?.trim() ? ` (${e.displayName.trim()})` : '';
                            const what = e.summary?.trim() ? ` — ${e.summary.trim()}` : '';
                            return `  ${e.id}${name}${what}`;
                        })
                            .join('\n') +
                        '\n\nPrefer one over answering yourself when the task is theirs — it carries instructions and tools ' +
                        'for that job that you do not, and it reports back to you. In particular: if you find yourself ' +
                        'reaching for a tool you do not have, and ' +
                        'the roster above says a specialist has that job, spawn THEM with its id in expert_id rather than ' +
                        'trying to route around the gap with the tools you do have — reading more will not produce a tool ' +
                        'that was never offered to you.',
                    cache: true,
                },
            ]
            : []),
    ];
}
/**
 * One conversation the user is having.
 *
 * `send` is the whole API. Everything else is state to read.
 */
export class Conversation extends EventTarget {
    /**
     * "This thread already has a name" — told by whoever read the archive.
     *
     * The latch above is per-OBJECT, and a resumed thread gets a fresh object: so a conversation that
     * was named on its first turn was renamed on the first message after every reload, and a rename
     * the person typed themselves was overwritten by a model the next time they said anything. Seen
     * across four turns of one thread as "Managing Sub Agents and Monitoring" → "Spawning and
     * Coordinating" → "Coordination of Sub Agents" → "Executing Sub Agents", each replacing the last
     * in the rail while the person watched.
     *
     * The archive knows, and answers with the title on the very read that resumes the thread. Taken
     * only from there and never from the local cache: a cached name that the server does not have
     * would suppress the thread's first title forever, which is the one failure worse than renaming.
     */
    markTitled() {
        this.titled = true;
    }
    /** What this thread has cost in latency: first-token on the last turn, and the mean round trip.
     *  Undefined until a turn has completed — showing 0 ms would be a lie, not a placeholder. */
    get metrics() {
        if (!this.timings.length)
            return { turns: 0, costUsd: this._costUsd, tokens: this._tokens };
        const withTtft = this.timings.filter((x) => x.ttftMs !== undefined);
        return {
            ttftMs: withTtft.length ? withTtft[withTtft.length - 1].ttftMs : undefined,
            avgMs: Math.round(this.timings.reduce((a, x) => a + x.totalMs, 0) / this.timings.length),
            costUsd: this._costUsd,
            tokens: this._tokens,
            turns: this.timings.length,
        };
    }
    /** Is this the turn currently on screen? Everything a stale turn produces is discarded — except
     *  what it COST, which was really spent and is recorded either way. */
    isCurrent(epoch) {
        return epoch === this.turnEpoch;
    }
    /**
     * THE LINE THE PERSON MIGHT SEND NEXT, asked for once this turn's answer is on the record.
     *
     * Not awaited by the turn, and never part of it: the answer is complete and shown before this
     * starts, so it cannot slow it, and a suggestion that fails is simply absent. Written by the main
     * model it would cost every turn its own length and land in the archive (`ai-worker` suggest.ts).
     *
     * Only onto the answer still being looked at. If another turn started meanwhile — the person typed,
     * or an agent's return was handed over — the moment has moved on, and that turn's answer gets its
     * own line.
     */
    /**
     * THE REST OF WHAT THE PERSON IS TYPING, for this thread — '' when there is no answer to follow.
     *
     * Asked only once the thread has turns: before its first message there is nothing on the record
     * to continue from, and the server has no conversation to read yet. The draft is the caller's to
     * time and to check for staleness (live-completion.ts); this only routes it.
     */
    async completeDraft(draft) {
        const ask = this.opts.completeDraft;
        if (!ask || !this._turns.length)
            return '';
        return ask(this.id, draft).catch(() => '');
    }
    async suggestNextFor(turn, epoch) {
        const ask = this.opts.suggestNext;
        if (!ask)
            return;
        const line = (await ask(this.id).catch(() => '')).trim();
        if (!line || !this.isCurrent(epoch) || this.busy || this._turns[this._turns.length - 1] !== turn)
            return;
        turn.nextReply = line;
        this.emit();
    }
    constructor(opts, id = `conv-${crypto.randomUUID()}`) {
        super();
        /** The model chosen for THIS conversation. Empty = whatever the org configured. */
        this.model = '';
        /** The VOICE model for this conversation. Held here for the same reason as `model` — the picker
         *  in the chat needs somewhere to put the choice, and a control with nowhere to write is a
         *  control that silently forgets. */
        this.voiceModel = '';
        /**
         * How hard the model should think, for THIS conversation.
         *
         * `medium` by default, which is what every turn already asked for when the value was hardcoded —
         * so making it settable changes who decides, not what happens by default.
         *
         * Read at SEND time rather than captured when the conversation opens, which is what lets it be
         * changed mid-thread: the next turn uses whatever it says now, and the turns already on screen
         * keep the effort they were actually run at.
         */
        this.effort = DEFAULT_EFFORT;
        /**
         * WHICH SPECIALIST IS ANSWERING, for this conversation.
         *
         * Empty means the ordinary assistant, which is what every thread was before this
         * existed — so the default changes nothing.
         *
         * Set, it changes three things at once, and that is the point of choosing one
         * rather than just picking a model: the worker resolves the specialist's PROMPT
         * (it is what an expert mostly is), the tool union narrows to what its role
         * allows, and the trail attributes the turn to it. A person who wanted only a
         * different model has a model picker; this is for wanting a different answerer.
         *
         * Read at SEND time like `effort`, so it can be changed mid-thread and the turns
         * already on screen keep whoever actually answered them.
         */
        this.expertId = '';
        /** True once a title has been requested — asking again on every turn would rename a thread the
         *  user may have renamed themselves. */
        this.titled = false;
        this.timings = [];
        /** The agent trees this conversation has started, by root run id. */
        this.trees = new Map();
        /** Trees this tab is currently advancing. One driver per tree — a second would bump the epoch and
         *  fence out the first, which looks like a run that randomly stops. */
        this.driving = new Set();
        /** Roots whose answer has already been brought back into this thread. Keyed by root run id, and
         *  never cleared — a return is reported once, and the poll that finds it fires every two seconds. */
        this.reported = new Set();
        /** Returns waiting for the thread to be free. Queued rather than dropped: an agent that finished
         *  while the user was mid-question still has something to say. */
        this.returns = [];
        this.draining = false;
        /** Set by `retire` when the thread is deleted. Nothing is written to it after that. */
        this.retired = false;
        /** Turn count at the last chapter pass. Zero means never — which, past the floor, means the next
         *  reply segments. */
        this.segmentedAt = 0;
        /** Why the last drive could not proceed, in prose. Read by the view; empty when all is well. */
        this.driveError = '';
        /**
         * IS THE RUN BEHIND THIS THREAD OVER?
         *
         * Set from the run tree by whoever is showing the thread, on every render — a mirror of the
         * server's answer, never a fact this object decides or keeps. False for a person's own thread,
         * which has no run behind it and is over only when they close the tab.
         *
         * It exists because this tab will otherwise run the model ON BEHALF of a dead agent: a child
         * finishing after its parent was stopped pushes its answer into the parent's thread, and the drain
         * takes a turn to hand it over. The header then said "Stopped" while the composer said "Thinking…"
         * with a Stop button under it — the run was over and the tab was still spending money in its name.
         */
        this.runFinished = false;
        this._turns = [];
        this._status = 'idle';
        /** The tools running right now, by name. A tool call is the longest silent pause in a turn — an
         *  interface that shows nothing during it looks broken — so the view can say what is happening. */
        this._running = [];
        this.inFlight = null;
        /*
         * WHICH TURN IS THE CURRENT ONE.
         *
         * Stop bumps it, and every closure a turn handed out captured the value it was started with — so
         * the moment somebody presses Stop, the deltas still arriving from a call that has not noticed
         * yet are dropped rather than painted. Before this, Stop cleared the flags and the text kept
         * appearing: the transport takes no abort signal, so nothing on the wire actually stopped.
         *
         * A counter rather than a boolean because turns overlap: pressing Stop and immediately asking
         * something else must not leave the new turn's deltas filtered by the old turn's flag.
         */
        this.turnEpoch = 0;
        /*
         * THE WATCHDOG — see TURN_SILENCE_MS.
         *
         * `lastSignal` is stamped in `emit`, which is the one place every sign of life in a turn passes
         * through: a text delta, a reasoning chunk, a tool row appearing, a tool result landing, a status
         * change. Watching there rather than at the model call is what makes this cover a hung TOOL as
         * well as a hung completion — the two look identical from the composer, and both were unkillable.
         */
        this.lastSignal = 0;
        this.watch = null;
        /** How many times the turn about to start has already been resumed — see `noteInFlight`. */
        this.resumes = 0;
        /**
         * The run id of the turn currently in flight, '' between turns.
         *
         * Read by the tool door so a tool call carries the same run as the model calls that asked for it.
         * Public because the manager dispatches tools and the conversation drives the turn — they are two
         * objects sharing one unit of work, and the id is the only thing they need to agree on.
         */
        this.currentRunId = '';
        /** When the turn on screen began — read by `stop`, which has no other way to time what it cuts. */
        this.turnStartedAt = 0;
        /**
         * How much of the live turn's `parts` the archive already holds — an index into them.
         *
         * A tool round writes its prose and its calls the moment they exist (see the loop), so by the time
         * a person presses Stop most of the turn is usually on the record already. Stop appends only what
         * is not: the tail from here on. It used to append the whole turn's text again, so a reloaded
         * thread showed the round's paragraph twice, once before its tool row and once after it.
         */
        this.archivedAt = 0;
        /** Bumped on every mutation. The renderer reads it to know something changed, which is what lets
         *  `turns` stay a STABLE array instead of being copied on every emit — see `emit`. */
        this._revision = 0;
        /**
         * Say something and run one turn.
         *
         * Resolves when the turn is finished OR has failed; it never rejects, because a failed turn is a
         * thing to RENDER, not an exception for a click handler to swallow.
         */
        /**
         * Load a resumed conversation's past turns.
         *
         * REPLACES rather than appends, and is refused once the thread is live: hydrating a conversation
         * that has already sent something would duplicate turns the user just watched arrive. Resume is a
         * one-shot at open time, which is the only moment the archive is authoritative over this object.
         *
         * The store's mirror is filled too, so the next turn's prompt carries the history — otherwise a
         * resumed thread would LOOK complete and the model would answer as if the conversation had just
         * begun, which is the worse half of the bug because nothing on screen shows it.
         */
        /**
         * True while a resumed thread's past is still in flight.
         *
         * Set by the manager, which hands the conversation over BEFORE the archive answers so opening is
         * instant. A view reads this to draw the thread's skeleton rather than an empty room — an empty
         * transcript and a not-yet-loaded one look identical otherwise, and telling a person a
         * conversation is empty when it is merely loading is the worse of the two lies.
         */
        this.hydrating = false;
        /**
         * True while what is on screen came from the LOCAL CACHE rather than from the archive.
         *
         * The cache exists to paint instantly and the archive is what is actually true, so the two are
         * raced — and the note beside that race claimed "`hydrate` REPLACES, so a cached transcript can
         * only ever be displaced by the real one, never the reverse." It does not: `hydrate` is a
         * one-shot and returns early the moment any turn exists, so whichever landed FIRST won, and the
         * cache almost always lands first — a local read is single-digit milliseconds against a round
         * trip.
         *
         * The result was a transcript frozen at whatever the cache last saw. Measured on a finished
         * sub-agent: the archive held thirty messages, the cache held the two it had been opened with
         * while the agent was still working, and the page showed those two — an agent that did nothing,
         * rendered from a fresh load, with no error and nothing to indicate anything was missing.
         *
         * The one-shot guard itself is right and stays: a thread this tab is AUTHORING is ahead of the
         * archive, and replacing it would throw away turns the person just watched arrive. This flag is
         * what separates the two cases — a cached snapshot is a placeholder for the archive and is
         * displaced by it; anything else is local truth and is not.
         */
        this.fromCache = false;
        /**
         * THE MIRROR BEING FILLED FROM THE ARCHIVE — what the resumed turn waits for.
         *
         * `prime` reads attached files and pictures on the way, so it finishes some ticks after the
         * thread is on screen — and the turn a reload interrupted is asked again in exactly that gap,
         * working on the mirror BEFORE it holds the thread. Observed: the retract found nothing to drop,
         * the question was stamped in an empty mirror, and then `prime` filled it, with the cut-off answer
         * back in and no time on the question.
         */
        this.primed = Promise.resolve();
        this.id = id;
        this.opts = opts;
        this.store = opts.store ?? memoryTranscriptStore();
    }
    /** The transcript, oldest first. A stable array — see `revision` for change detection. */
    get turns() {
        return this._turns;
    }
    /** Increments on every mutation, including in-place streaming writes. A renderer that memoises
     *  should key on this rather than on array identity. */
    get revision() {
        return this._revision;
    }
    get status() {
        return this._status;
    }
    /** True while a turn is in flight — the composer should be disabled, nothing more.
     *  `working` counts: a tool is running, the turn has not finished, and a second question sent now
     *  would race the first. */
    get busy() {
        return this._status === 'thinking' || this._status === 'streaming' || this._status === 'working';
    }
    /** The tools running right now, by name — so the view can say "running writeSymbols…" instead of
     *  showing a spinner for the several seconds a real tool takes. */
    get running() {
        return this._running;
    }
    /**
     * Paint what the last visit saw, while the archive is still in flight.
     *
     * Deliberately not `hydrate`: the caller is offering a GUESS, and marking it as one is what lets
     * the real read replace it. A cache write that went through `hydrate` would be indistinguishable
     * from live content and would therefore be permanent.
     */
    applyCached(turns) {
        if (this._turns.length > 0)
            return;
        this.applyArchive(turns);
        this.fromCache = true;
    }
    /*
     * A RELOAD MID-ANSWER IS NOT AN ENDING.
     *
     * A top-level chat turn is driven by the tab that sent it and is not an agent run, so nothing
     * on the server carries it on when that tab goes away. Refresh the page while the model was
     * thinking and the thread came back as the question with nothing under it, and stayed that way:
     * the answer had died with the tab, and no sweep would ever look for it.
     *
     * The tab leaves itself a note instead. sessionStorage survives a reload of the SAME tab and
     * nothing else — not a second tab on the same thread, which is exactly the boundary wanted, since
     * two tabs each resuming the same turn would answer it twice. The note is written when a turn
     * starts and removed when it ends; a note still there when the archive comes back with the
     * question as its last turn is a turn this tab abandoned, and it is asked again from the archive
     * without re-appending the question ('again'). Old notes are ignored: after ten minutes the
     * person has moved on, and a stale answer arriving unasked is worse than none.
     */
    static { this.RESUME_MAX_AGE_MS = 10 * 60_000; }
    static inFlightKey(id) {
        return `ai:inflight:${id}`;
    }
    /**
     * …AND HOW MANY TIMES IT HAS ALREADY BEEN PICKED UP.
     *
     * The note used to be a bare timestamp, re-stamped by the resume itself — so every reload inside
     * the ten-minute window asked the same question again and left a fresh note behind it. A turn that
     * hangs and a reader who reloads to see whether it has finished are therefore a LOOP: thinking,
     * reload, thinking, reload, for as long as they keep looking. Reported exactly that way.
     *
     * One resume per turn. A reload mid-answer is a mishap worth recovering from; the same answer
     * failing to arrive twice is an answer that is not coming, and asking a third time only spends
     * money to make the reader wait again.
     */
    noteInFlight(tries = 0) {
        try {
            sessionStorage.setItem(Conversation.inFlightKey(this.id), JSON.stringify({ at: Date.now(), tries }));
        }
        catch {
            /* storage refused (private mode, quota) — the resume is a convenience, not a record */
        }
    }
    /** The note, tolerating the bare-timestamp shape an older tab may have left behind. */
    static readNote(id) {
        let raw = '';
        try {
            raw = sessionStorage.getItem(Conversation.inFlightKey(id)) ?? '';
        }
        catch {
            return null;
        }
        if (!raw)
            return null;
        const n = Number(raw);
        if (Number.isFinite(n) && n > 0)
            return { at: n, tries: 0 };
        try {
            const o = JSON.parse(raw);
            return typeof o.at === 'number' ? { at: o.at, tries: Number(o.tries) || 0 } : null;
        }
        catch {
            return null;
        }
    }
    clearInFlight() {
        try {
            sessionStorage.removeItem(Conversation.inFlightKey(this.id));
        }
        catch {
            /* nothing to clear, or nowhere to clear it from */
        }
    }
    resumeAbandonedTurn() {
        const note = Conversation.readNote(this.id);
        if (!note)
            return;
        this.clearInFlight();
        if (Date.now() - note.at > Conversation.RESUME_MAX_AGE_MS || this.busy)
            return;
        // ONCE. A second attempt at a question that has already failed to answer once is how a hung
        // turn survives every reload — see `noteInFlight`.
        if (note.tries >= 1)
            return;
        this.resumes = note.tries + 1;
        // The question is the last user turn. Anything after it is what the page-hide stop left
        // behind — an answer cut short, or nothing at all. An answer with words in it stands; an
        // empty one is dropped from the screen and the prompt (the archive keeps it) and the
        // question is asked again.
        const at = this._turns.map((x) => x.role).lastIndexOf('user');
        if (at < 0)
            return;
        const question = this._turns[at];
        const after = this._turns.slice(at + 1);
        // A tool the page-hide stop cut off is not an answer either; one that finished is.
        const settled = (tool) => tool.state === 'ok' || (tool.state === 'error' && tool.message !== STOPPED_TOOL_MESSAGE);
        if (!question.text || after.some((x) => x.text.trim() || x.tools?.some(settled)))
            return;
        if (after.length) {
            this._turns = this._turns.slice(0, at + 1);
            this.emit();
        }
        void (async () => {
            await this.primed;
            await this.store.retract?.(this.id);
            await this.send(question.text, 'again');
        })();
    }
    hydrate(turns) {
        // Anything already here that came from the archive or from this tab's own turns is authoritative
        // over a second read; a cached PLACEHOLDER is not, and is exactly what this call replaces.
        if (this._turns.length > 0 && !this.fromCache)
            return;
        this.applyArchive(turns);
        // Here, not in `applyArchive`: the cached snapshot goes through that too, and it is the thread as
        // it was when last OPENED, not as it was last answered — resumed from it, a specialist switched
        // since would come back as the old one, and the archive could not correct it, because by then
        // the field looks chosen. Before the abandoned turn, which is asked again with what this sets.
        this.resumeSettings();
        this.emit();
        this.resumeAbandonedTurn();
    }
    /**
     * Re-read the archive for a thread this tab WATCHES rather than drives.
     *
     * `hydrate` is a one-shot on purpose: for a thread this tab is authoring, the local object is
     * ahead of the archive and replacing it would throw away turns the archive has not caught up to
     * yet. For a sub-agent's thread, the relationship is the other way round — another driver is
     * writing it and the archive is the only thing that knows what has happened. There is no live
     * channel for a single transcript (LIVE_TOPIC_TRANSCRIPTS is the conversation LIST), so the view
     * showed a snapshot taken when it opened and nothing after it: a tool call that failed while you
     * watched appeared only if you left the page and came back.
     *
     * So this replaces, deliberately, and is only ever called for a thread nobody here is driving.
     */
    rehydrateWatched(turns) {
        this.applyArchive(turns);
    }
    applyArchive(turns) {
        // Every archive read clears the flag HERE rather than at each call site, so a future third way
        // of applying archived turns cannot forget to and leave the object claiming to hold a guess.
        this.fromCache = false;
        // `reasoning` only when there IS some — an empty string would give every resumed turn a
        // disclosure to open with nothing behind it (see Turn.reasoning: absent ≠ empty).
        this._turns = turns.map((x) => ({
            role: x.role,
            text: x.text,
            ...(x.seq !== undefined ? { seq: x.seq } : {}),
            ...(x.reasoning ? { reasoning: x.reasoning } : {}),
            ...(x.reasoningMs !== undefined ? { reasoningMs: x.reasoningMs } : {}),
            ...(x.tools?.length ? { tools: x.tools } : {}),
            // The order it happened in. Everything below derives from it; nothing below may contradict it.
            ...(x.parts?.length ? { parts: x.parts } : {}),
            /*
             * AND THE FILES THE TURN PRODUCED — a picture, a chart, an export.
             *
             * Every other field of a turn is rebuilt here and this one was not, so a generated image was
             * archived correctly, folded correctly, and then dropped on the way into the object that
             * renders it. The live thread showed the picture; reloading the page showed the same turn
             * with the picture gone, and no chip either, because there was nothing left to say a file had
             * ever existed. The bytes were in the blob store the whole time.
             *
             * The one failure mode this file exists to prevent, in its own construction path: two ways of
             * building a turn, one of them populating half of it.
             */
            ...(x.files?.length ? { files: x.files } : {}),
            ...(x.references?.length ? { references: x.references } : {}),
            ...(x.settings ? { settings: x.settings } : {}),
            ...(x.spawnedRoots?.length ? { spawnedRoots: x.spawnedRoots } : {}),
            // A peer's note keeps its attribution across a reload. Without this, a reopened thread shows
            // the reader a sentence in their own bubble that another agent wrote — the live view says one
            // thing and the resumed view says another, about the same record.
            ...(x.fromRunId ? { fromRunId: x.fromRunId } : {}),
            // And a child's answer keeps ITS treatment too. Ordered after `fromRunId` because both are
            // set on a return and `renderTurn` reads this one first — a return is not a note, and a
            // reader has to be able to tell "the job you were waiting on came back" from "a colleague
            // said this".
            ...(x.returned ? { returned: true } : {}),
            // Cut short by the reader — says so after a reload too.
            ...(x.telemetry?.stopped ? { stopped: true } : {}),
        }));
        /*
         * REBUILD THE DERIVED STATE, not just the rows.
         *
         * This is the parity fix. A live conversation accumulates timings, cost and agent trees as it
         * runs; hydrate rebuilt only the visible turns, so reopening a thread silently dropped its TTFT,
         * its average, its running cost and every tree — and the header refilled only after another
         * message. Two construction paths, one of them populating half the state.
         *
         * Everything below is derived from the SAME archived rows the turns come from, so the two paths
         * now end in the same place rather than being kept in step by hand.
         */
        for (const x of turns) {
            /*
             * A RETURN ALREADY IN THE THREAD HAS ALREADY BEEN REPORTED.
             *
             * `reported` is what stops the two-second poll turning one finished agent into an endless
             * conversation with itself — and it lived only in memory, so every reopen re-reported every
             * finished agent the thread had ever started. The model was handed the same return a second
             * time and had to reconcile it against the first: "the probe agent sent one more completion
             * notice… both say READY". A wasted turn, a duplicate written permanently into the archive,
             * and it happened once per reload per agent.
             *
             * Seeded from the record instead, which is the only place that survives a reload. The row says
             * which run wrote it, so this is exact — no matching on prose, and nothing to get wrong when a
             * thread has several agents that finished with similar answers.
             */
            if (x.returned && x.fromRunId)
                this.reported.add(x.fromRunId);
            if (x.telemetry) {
                this.timings.push({ ttftMs: x.telemetry.ttftMs, totalMs: x.telemetry.totalMs });
                if (x.telemetry.costUsd !== undefined)
                    this._costUsd = (this._costUsd ?? 0) + x.telemetry.costUsd;
                // Tokens too. Rebuilt beside cost because they are the same kind of fact and the header
                // shows them side by side — without this, every resumed thread, and so EVERY sub-agent
                // view, had a cost badge and no token badge and looked half-measured.
                if (x.telemetry.tokens !== undefined)
                    this._tokens = (this._tokens ?? 0) + x.telemetry.tokens;
            }
            // Follow — and resume driving — anything this thread started. An agent left running is still
            // running; reopening the conversation should show it, not pretend it never happened.
            for (const root of x.spawnedRoots ?? [])
                this.followSpawned(root);
        }
        // `prime`, never `append`: these turns came FROM the archive, and appending them would write the
        // whole transcript back on every resume — doubling it each time the thread was opened.
        // Held, not awaited: the turns are on screen now, and only what reads or writes the mirror waits.
        // A failure is said, not thrown at the waiter: the turn still runs, and an empty mirror is filled
        // from the archive when it is stamped (see `durableTranscriptStore.stamp`).
        this.primed = Promise.resolve(this.store.prime?.(this.id, turns)).catch((err) => console.warn('[ai] the prompt mirror could not be filled from the archive:', err));
        this.emit();
    }
    /**
     * WHO THE THREAD WAS TALKING TO, picked back up from its record.
     *
     * The specialist, model and effort lived only in this object, so a reload or a reopen fell back to
     * the default specialist — and the next turn, including a question the reload interrupted and asks
     * again, was answered by another specialist with another prompt and other tools, while the thread
     * looked the same. The last turn that says (a question, or an answer: whichever is newer) is the
     * thread's current choice; a note an agent wrote says nothing about it and is passed over.
     *
     * Only where nobody has chosen since the thread opened: an empty specialist or model, effort still
     * at its default. Opening reads the archive asynchronously, and a choice made meanwhile wins.
     */
    resumeSettings() {
        const last = [...this._turns].reverse().find((x) => x.settings && !x.fromRunId)?.settings;
        if (!last)
            return;
        if (!this.expertId && last.expertId)
            this.expertId = last.expertId;
        if (!this.model && last.model)
            this.model = last.model;
        if (this.effort === DEFAULT_EFFORT && last.effort)
            this.effort = last.effort;
    }
    /** The specialist, model and effort the next turn would be asked of — what a turn records. */
    settingsNow() {
        return {
            ...(this.specialist() ? { expertId: this.specialist() } : {}),
            model: this.model || this.opts.defaultModel || 'openrouter/auto',
            effort: this.effort,
        };
    }
    /**
     * Send a message and take a turn.
     *
     * `from` says who is speaking. `'user'` is a person typing. `'agent'` is a subagent's answer
     * arriving after the fact — it reaches the model as input, because that is what it is, but it is
     * NOT drawn as the user's words and never pretends someone typed it. The distinction is carried in
     * the text as well as in the turn, so a transcript read back later still says where the sentence
     * came from.
     */
    /**
     * Take the last turn again.
     *
     * Retry is not "send that message twice". It drops the answer you did not want and asks again
     * from the same question — so what is re-run is the trailing assistant turn, and the user's
     * message is neither re-drawn nor re-appended. A retry that re-sent the prompt would leave the
     * thread reading as though the person had asked the same thing two or three times, which is a
     * worse record than the answer they were trying to replace.
     *
     * The ARCHIVE keeps the discarded answer. It happened, the org was billed for it, and the audit
     * trail is not ours to edit — only the live thread and the prompt the next call is built from
     * forget it. Refused while a turn is in flight, and on a thread whose last turn is not an answer.
     */
    async retryLast() {
        if (this.busy)
            return;
        const last = this._turns.at(-1);
        if (!last || last.role !== 'assistant')
            return;
        const question = [...this._turns].reverse().find((x) => x.role === 'user');
        if (!question?.text)
            return;
        this._turns = this._turns.slice(0, -1);
        this.emit();
        // The abandoned answer leaves the PROMPT as well as the screen, or the new one continues it.
        await this.store.retract?.(this.id);
        await this.send(question.text, 'again');
    }
    /**
     * WHO IS ANSWERING — resolved, not read raw.
     *
     * `expertId` is empty until somebody picks from the dropdown, but the dropdown
     * SHOWS the default specialist the whole time. Reading the raw field meant the
     * label and the reach disagreed for every thread nobody touched the picker on
     * (see effectiveSpecialist). Both sides resolve the same way now.
     */
    specialist() {
        return effectiveSpecialist(this.expertId, this.opts.experts?.() ?? []);
    }
    /**
     * The tool union, narrowed to the specialist answering.
     *
     * The same `scopeToExpert` an agent run uses, deliberately — a specialist that
     * could do more in a chat than it can in a spawned run would make its role a
     * description of one path rather than of the specialist.
     *
     * No specialist AND no default in the roster means the full union: a thread
     * must not silently lose its tools because a roster changed underneath it, and
     * an empty result would look like the assistant had broken.
     */
    scopedTools() {
        const all = this.opts.tools();
        return scopeToExpert(all, this.specialist(), this.opts.experts?.() ?? []);
    }
    /**
     * SAY SOMETHING TO THE AGENT THIS THREAD BELONGS TO — and take no turn of this tab's own.
     *
     * A message in a live agent's thread went to `send`, which ran this tab's model on it. The server
     * also hands every new message in that thread to the run at its next step, so one message had two
     * answerers writing one thread — and the tab's, which never saw the task, answered first. Stop
     * then stopped the tab's turn and left the run going, which is why the first press looked dead.
     *
     * So the words are written and nothing here replies: the run that owns the thread reads them at
     * its next step. Returns the reason in prose when they could not be written, '' when they were.
     */
    async steer(text, attachments = [], references = []) {
        const trimmed = text.trim();
        if (!trimmed)
            return '';
        // Uploaded before anything is shown, so a message that could not go never appears to have gone.
        const files = [];
        for (const file of attachments) {
            const ref = this.store.upload ? await this.store.upload(this.id, file) : null;
            if (!ref)
                return this.store.upload
                    ? `Could not attach ${file.name}. The message was not sent — try again, or send it without the file.`
                    : `Attachments are not kept on this surface, so ${file.name} could not be sent.`;
            files.push(ref);
        }
        const user = {
            role: 'user',
            text: trimmed,
            ...(files.length ? { files } : {}),
            ...(references.length ? { references: [...references] } : {}),
        };
        this.push(user);
        try {
            const seq = await this.store.append(this.id, 'user', trimmed, undefined, undefined, undefined, user.files, undefined, user.references);
            if (seq !== undefined)
                user.seq = seq;
        }
        catch (e) {
            this._turns = this._turns.filter((x) => x !== user);
            this.emit();
            return e instanceof Error ? e.message : String(e);
        }
        this.emit();
        return '';
    }
    /**
     * Take a turn.
     *
     * RETURNS WHETHER IT TOOK ONE, and that is not a convenience — it is the difference between a
     * queued agent return being delivered and being destroyed. A caller holding something that MUST
     * be said (see `drainReturns`) cannot tell a refusal from a completed turn if both are `void`,
     * so it drops what it was carrying and nothing ever says it again.
     *
     * `false` means the turn was refused before anything happened: empty text, or the thread is
     * already busy. Nothing was appended, nothing was sent, and the caller still owns whatever it
     * was trying to say.
     */
    async send(text, from = 'user', 
    /** Which run's answer this is, when `from` is 'agent'. Written onto the record so a reopened
     *  thread can still tell an agent's answer from the reader's own words. */
    fromRunId = '', 
    /** Files the person attached to this message. Uploaded before the message is written, so the
     *  record never names a file the store does not hold. */
    attachments = [], 
    /** What the person referenced with @ — the links are already in `text`. */
    references = []) {
        const trimmed = text.trim();
        if (!trimmed || this.busy || this.retired)
            return false;
        // CLAIM THE TURN SYNCHRONOUSLY, before the first await.
        //
        // The guard above reads `busy`, and `busy` used to be set after an `await` on the store — so two
        // callers both cleared the guard before either set the flag, and a double-clicked Send ran two
        // turns against one question. Nothing may yield between testing the condition and making it
        // false; everything below is ordinary async work that a second caller is now correctly refused.
        this.setStatus('thinking');
        this.inFlight = new AbortController();
        /*
         * THE NOTE IS FOR A QUESTION A PERSON ASKED. NOTHING ELSE.
         *
         * It was written for every turn, and an agent's thread takes turns constantly — a child returns,
         * the parent is handed the answer, the tab drives the turn. Those are not the tab's to carry:
         * the run is durable on the server and has its own driver, which is the whole reason a tree
         * survives a closed tab. Noting them meant every reload re-asked a question the run was already
         * handling, so an agent thread that was doing nothing came back saying "Thinking…" with a Stop
         * button under it, over and over, for as long as anybody kept reloading. That is the ghosting.
         *
         * A person's own message is the one case the comment above was ever written for: nothing on the
         * server carries it, so if this tab drops it, it is gone. `again` is a resume of exactly that, so
         * it carries the count forward and the cap in `resumeAbandonedTurn` stops the second retry.
         */
        if (from !== 'agent')
            this.noteInFlight(this.resumes);
        this.resumes = 0;
        // This turn's identity, captured before anything async. Stop bumps the counter; everything
        // below compares against this value to decide whether it is still the turn on screen.
        const epoch = ++this.turnEpoch;
        // The last answer's suggestion is for the moment that just ended: whatever started this turn —
        // the person's own words, a retry, an agent's return — has moved past it.
        for (const t of this._turns)
            delete t.nextReply;
        // Who this turn is asked of, captured once: the question and its answer record the same three,
        // and a picker moved while the turn runs changes the next turn, not this one.
        const settings = this.settingsNow();
        this.turnSettings = settings;
        // As soon as the turn is ours, before uploads and the archive: what this thread's specialist will
        // reach for slowly starts getting ready while the model reads the question. See `prewarmForExpert`.
        if (toolsUsable(this.opts, this.store))
            prewarmForExpert(this.specialist(), this.opts.experts?.() ?? [], this.id);
        // 'again' means THE QUESTION IS ALREADY THERE — in the thread on screen and in the archive.
        // Two callers need it and both would otherwise duplicate the message: a retry (same question,
        // new answer) and a branch (the server already appended the edited message to the new version).
        // The model reads the mirror rather than this argument, so not appending is not a loss.
        const again = from === 'again';
        const user = again
            ? undefined
            : {
                role: 'user',
                text: trimmed,
                ...(from === 'agent' ? { returned: true, ...(fromRunId ? { fromRunId } : {}) } : {}),
                ...(from === 'user' && references.length ? { references: [...references] } : {}),
                // A person's question says who it was asked of; an agent's note says nothing about it.
                ...(from === 'user' ? { settings } : {}),
            };
        if (user)
            this.push(user);
        const assistant = { role: 'assistant', text: '', streaming: true, settings };
        this.push(assistant);
        /*
         * THE ATTACHMENTS GO FIRST, and a file that did not go is said so.
         *
         * The composer collected these and, until this existed, dropped them on the floor between the
         * chip and the send — the person saw their file leave with the message and the model was told
         * nothing, so it insisted there was no attachment. Uploading before the append is what the
         * door demands (a message may only name a blob the store holds); refusing the turn on a
         * failed upload is what honesty demands — a question about a file the model never got is not
         * the question that was asked.
         */
        if (user && attachments.length) {
            const failed = [];
            for (const file of attachments) {
                const ref = this.store.upload ? await this.store.upload(this.id, file) : null;
                if (ref)
                    (user.files ??= []).push(ref);
                else
                    failed.push(file.name);
            }
            if (failed.length) {
                assistant.streaming = false;
                assistant.error = this.store.upload
                    ? `Could not attach ${failed.join(', ')}. The message was not sent — try again, or send it without the file.`
                    : `Attachments are not kept on this surface, so ${failed.join(', ')} could not be sent.`;
                this.setStatus('error');
                this.inFlight = null;
                this.clearInFlight();
                return true;
            }
            this.emit(); // the chips, now with their store identity
        }
        if (user) {
            /*
             * A RETURN IS WRITTEN AS A RETURN, not as another user message.
             *
             * It goes to the same slot — the model reads that slot — but it carries the run that produced
             * it and an id derived from that run. The author is what stops a reopened thread drawing it as
             * words the reader typed; the stable id is what stops the same finished agent being reported
             * twice. Falls back to a plain append when the store cannot express authorship, which loses
             * the distinction rather than the message.
             */
            const userSeq = from === 'agent' && fromRunId && this.store.appendReturn
                ? await this.store.appendReturn(this.id, trimmed, fromRunId)
                : await this.store.append(this.id, 'user', trimmed, undefined, undefined, undefined, user.files, undefined, user.references, from === 'user' ? settings : undefined);
            if (userSeq !== undefined)
                user.seq = userSeq;
        }
        // WHEN IT WAS ASKED, on the message that starts this turn: the one just written, or — a retry,
        // a resume, an edited question — the one already there, which is being asked now. Once, before
        // the first call, so every call of the turn reads the same line. See `sentAtLine`.
        await this.store.stamp?.(this.id, sentAtLine(new Date()));
        /*
         * NAME IT FROM THE QUESTION, not the answer. This used to wait for `reply.text` — the model's
         * own final reply — which meant a turn that spent its whole step budget on tool calls before
         * producing text (the ordinary shape of a hard request) sat untitled for the entire turn, right
         * when a person is most likely to be watching the thread list wondering if anything is
         * happening. The person's OWN opening message is already the best one-line summary available,
         * and it is on the server the moment `store.append` above returns — so ask for the title now,
         * in parallel with the turn, instead of after it.
         */
        if (user && !this.titled) {
            this.titled = true;
            void this.opts
                .autoTitle?.(this.id)
                .then((title) => {
                if (title)
                    this.opts.onTitled?.(title);
            })
                .catch(() => undefined); // a nameless thread is a cosmetic loss, never a failed turn
        }
        try {
            // A chat turn is a CHAT CALL, not an agent run.
            //
            // `makeAgentStep` wraps this same primitive in agent-TREE semantics — a run id, dangling tool
            // reconciliation, headline-and-done. A person typing in a box has none of that, and reaching
            // for the step here meant passing a fabricated `AgentRun` and reading a reply field that does
            // not exist. Same transport, same metering, same credit gate; one layer down.
            // The user's pick for THIS conversation wins over the org default. Dropping it was the reason
            // choosing DeepSeek still reached Gemini: the choice was collected, displayed, and discarded.
            const model = settings.model;
            const startedAt = Date.now();
            this.turnStartedAt = startedAt;
            // A new turn measures itself from zero. See turnCostUsd: absent stays absent, so these reset
            // to undefined rather than to 0.
            this.turnCostUsd = undefined;
            this.turnTokens = undefined;
            /*
             * WHICH DOOR THIS MODEL GOES THROUGH — derived from the id, not assumed.
             *
             * This was hardcoded to 'openrouter' for every turn, which meant picking any natively-served
             * vendor posted that vendor's own model id to the aggregator. OpenRouter does not recognise a
             * non-namespaced id, so it answered 400 and the turn failed — Claude, GPT and Gemini all
             * unusable, while the deployment's native provider keys went untouched by the chat path.
             *
             * `routeFor` is the SAME rule the worker routes on, in the package both import, so the browser
             * cannot decide one thing while the edge decides another. Named in both places still, for the
             * reason the old comment gave: `edgeAdapter` decides where the call goes and `tanstackChat`
             * decides how the reasoning request is spelled for it, and letting those two default
             * independently is how they drift into asking OpenRouter in Anthropic's dialect.
             */
            const provider = routeFor(model);
            /*
             * ONE RUN ID PER TURN — the thing that makes a turn legible after the fact.
             *
             * A turn is one question and, because of the tool loop below, often several model calls. The
             * server sees those calls one at a time and has no way to know they were one piece of work,
             * which is why the bill and the trail both carried an empty `run_id` column from the day they
             * were added. Minting it HERE, where the turn actually begins, is the only place that knows.
             *
             * NOT the conversation id: a thread has many turns, and grouping by it would say a year of
             * conversation was one run. Per-turn is the unit a person would point at and call "what it
             * did when I asked that".
             */
            const runId = `${TURN_RUN_PREFIX}${crypto.randomUUID()}`;
            // Published for the duration of the turn so the tool door can stamp it too — a tool call and
            // the model calls that asked for it are the same run, and attributing only half of it would
            // leave the trail showing thinking with no actions or actions with no thinking.
            this.currentRunId = runId;
            // The provider stack loads on the first turn a tab sends, not with the page — see the note
            // on `@vitesse/agent-loop/chat`. A tab that only reads never pays for it.
            const wire = this.opts.chat ? null : await import('@vitesse/agent-loop/chat');
            const chat = this.opts.chat ??
                wire.tanstackChat({
                    provider,
                    adapter: wire.edgeAdapter({
                        provider,
                        baseUrl: this.opts.aiUrl,
                        model,
                        token: this.opts.token,
                        conversationId: this.id,
                        platform: this.opts.platform,
                        runId: () => runId,
                        // What makes the worker resolve this specialist's prompt for the turn. RESOLVED, so the
                        // prompt the worker builds is the same specialist the tools were scoped to above.
                        expertId: this.specialist() || undefined,
                        trigger: 'chat',
                    }),
                });
            /*
             * THE TOOL LOOP.
             *
             * A turn that called a tool has NOT finished thinking — it is progress, not a conclusion. The
             * model asked for something and has to be called again with the answer, so one `send` may be
             * several model calls: ask, run, ask again with the result, until it replies with prose.
             *
             * Same two-phase discipline the agent loop uses, for the same reason: the call is recorded
             * BEFORE it runs, so a tab that dies mid-tool leaves a record saying "this was attempted",
             * which the next reader takes as UNKNOWN rather than as never-happened. Recording after would
             * make a crashed write to a controller indistinguishable from one that never fired.
             *
             * WHY HERE AND NOT IN `makeAgentStep`. That wraps this same primitive in agent-TREE semantics
             * — a run id, a durable budget, headline-and-done, dangling reconciliation across drivers. A
             * person typing in a box has none of those; the earlier attempt to reach for it meant
             * fabricating an `AgentRun`. Same transport, same metering, same credit gate, one layer down.
             */
            const useTools = toolsUsable(this.opts, this.store);
            /*
             * WHAT THIS TURN HAS ALREADY SAID, across the rounds before this one.
             *
             * Every round after a tool call is a fresh model call with its own text, and the turn is all
             * of them. Held here rather than read off the turn so that a round which produces nothing —
             * a call that only asks for another tool — cannot leave a trailing blank between paragraphs.
             */
            let roundAt = turnParts(assistant).length;
            this.archivedAt = roundAt;
            let reply = await this.withUpstreamRetry(epoch, async () => chat(await this.store.history(this.id), {
                model,
                vision: seesImages(model),
                effort: this.effort,
                systemPrompts: standingPrompts(this.opts.experts?.()),
                tools: useTools ? this.scopedTools() : undefined,
                onDelta: this.streamInto(assistant, epoch),
                onReasoning: this.reasoningInto(assistant, epoch),
                onToolCall: this.announceTool(assistant, epoch),
                onToolArgs: this.streamToolArgs(assistant, epoch),
            }));
            // Not after a stop: the call resolving late must not write its whole answer over the partial
            // the reader stopped at. `addCost` still runs — the spend is real either way.
            if (this.isCurrent(epoch))
                this.settleRound(assistant, roundAt, reply);
            this.addCost(reply.usage?.costUsd);
            this.addTokens(reply.usage);
            for (let round = 0; useTools && this.isCurrent(epoch) && reply.toolCalls?.length && round < MAX_TOOL_ROUNDS; round++) {
                // Visible while it runs. A silent pause here is the model's longest — a tool can take
                // seconds — and an interface that shows nothing during it looks broken.
                this._running = reply.toolCalls.map((c) => c.name);
                this.setStatus('working');
                // PHASE 1 — before anything runs, on screen as well as in the archive.
                //
                // The visible row is written here rather than on completion for the same reason the durable
                // one is: a call that was dispatched and never resolved is a fact, and a UI that only draws
                // finished calls would show nothing at all for a tool that hung — the exact case where the
                // person most needs to see what is happening.
                // Reconciled, not appended: `announceTool` has usually already put these rows up as the
                // model named them, and pushing again would draw every call twice. What arrives here that
                // the announcement did not have is the ARGUMENTS, so an existing row is filled in place.
                {
                    /*
                     * THE REPLY IS AUTHORITATIVE. An announced row the reply does not name — an id the
                     * provider renumbered, a re-drained retry — would otherwise stay on screen forever as a
                     * call with no arguments and no outcome: observed as an "Asked you" card with no question
                     * that read WAITING FOR YOU after the real one had been answered.
                     */
                    const rows = (assistant.tools ?? []).filter((x) => x.state !== 'running' || reply.toolCalls.some((c) => c.id === x.id));
                    const kept = new Set(rows.map((x) => x.id));
                    const parts = turnParts(assistant);
                    // An orphaned row leaves the sequence too, or the turn would draw a hole where it was.
                    for (let i = parts.length - 1; i >= 0; i--) {
                        const p = parts[i];
                        if (p.kind === 'tool' && !kept.has(p.id))
                            parts.splice(i, 1);
                    }
                    for (const c of reply.toolCalls) {
                        const seen = rows.find((x) => x.id === c.id);
                        if (seen) {
                            seen.name = c.name;
                            seen.argumentsJson = c.argumentsJson;
                            seen.state = 'running';
                        }
                        else {
                            rows.push({ id: c.id, name: c.name, argumentsJson: c.argumentsJson, state: 'running' });
                            this.closeThinking(assistant);
                            parts.push({ kind: 'tool', id: c.id });
                        }
                    }
                    assistant.tools = rows;
                }
                this.emit();
                /*
                 * WHAT THIS ROUND SAID, AND WHAT IT THOUGHT — archived NOW, on the rows they belong to.
                 *
                 * Only the final round's prose was written, at the end; a sentence spoken before a tool
                 * call ("I'll hand this to the specialist…") existed on screen and nowhere else, and the
                 * thinking of a round that called tools had no row at all. Reloaded, the thread was its
                 * last paragraph and its calls. Same shape as the agent loop's step: a prose row carries the
                 * round's thinking when there is prose, the phase-1 row carries it when there is not.
                 */
                const spoke = reply.text.trim().length > 0;
                if (spoke)
                    await this.store.append(this.id, 'assistant', reply.text, reply.reasoning, reply.reasoningMs);
                await this.store.appendToolCalls(this.id, reply.toolCalls, spoke ? undefined : reply.reasoning, spoke ? undefined : reply.reasoningMs);
                for (const call of reply.toolCalls) {
                    // Stopped between two calls of one round: the rest are not run. `stop()` already answered
                    // every call of the round as stopped, so there is nothing left to write for them.
                    if (!this.isCurrent(epoch))
                        break;
                    /*
                     * THE DOOR, NARROWED THE WAY `scopedTools()` NARROWS THE OFFER.
                     *
                     * A chat turn answers as a specialist too — the one the picker chose, or the surface's
                     * default — so a model naming a tool that specialist was never offered has to be refused
                     * here rather than run. Only the offer was scoped before, which made the subset a
                     * description of what a model would normally reach for rather than a limit on it.
                     *
                     * The same `gateToExpert` a spawned run's door uses, so one specialist cannot reach
                     * further in a chat than it can in a run — the property `scopedTools()` above exists for.
                     *
                     * `dispatch` never throws by contract. Catching anyway keeps a dispatcher that DID from
                     * stranding a phase-1 record — the outcome below is what resolves it, and skipping it
                     * would leave the call permanently unknown.
                     */
                    const outcome = !gateToExpert(call, this.specialist(), this.opts.experts?.() ?? [])
                        ? outsideSubset(call.name)
                        : await this.opts.dispatch(call, { runId: this.currentRunId, epoch: 0n, conversationId: this.id }).catch((e) => ({
                            ok: false,
                            message: `"${call.name}" could not be run: ${e instanceof Error ? e.message : String(e)}`,
                        }));
                    // An agent started is an agent running, whether or not the turn that asked for it was
                    // stopped while it started (a stop does not reach what a turn began). So it is noted
                    // either way — that is what puts it on screen, where the person can stop it too.
                    this.noteSpawn(assistant, outcome);
                    /*
                     * A TOOL THAT RETURNS AFTER STOP CHANGES NOTHING.
                     *
                     * `stop()` marked this call stopped and wrote that as its result; the outcome arriving now
                     * was painted over the stopped row, written as a SECOND result for the same call, set the
                     * thread back to "thinking" and started another paid model call nobody asked for. The real
                     * outcome is in the audit trail, which the dispatch writes on its own.
                     */
                    if (!this.isCurrent(epoch))
                        break;
                    // PHASE 2 — resolves both records, the durable one and the visible one.
                    const row = assistant.tools?.find((x) => x.id === call.id);
                    if (row) {
                        row.state = outcome.ok ? 'ok' : 'error';
                        row.message = outcome.message;
                        row.data = outcome.data;
                    }
                    this.emit();
                    const record = await this.store.appendToolResult(this.id, call.id, outcome);
                    /*
                     * THE ARTIFACT, ON THE TURN THAT MADE IT.
                     *
                     * By sha, and de-duplicated, because the store is content-addressed: a chart produced
                     * twice in one turn is one file, and drawing it twice would read as two.
                     */
                    const made = record?.files ?? [];
                    if (made.length) {
                        const have = assistant.files ?? [];
                        assistant.files = [...have, ...made.filter((f) => !have.some((x) => x.sha256 === f.sha256))];
                        this.emit();
                    }
                }
                this._running = [];
                // And the round ends without another call: see above.
                if (!this.isCurrent(epoch))
                    break;
                this.setStatus('thinking');
                // Round + 1 is about to be the LAST. Telling the model so is what turns a cap from a silent
                // truncation into a constraint it can work within — it answers with what it has instead of
                // asking for a ninth tool it will never get.
                //
                // TELLING IS NOT ENOUGH: the last call also offers no tools, as the agent loop's last step
                // does (`ctx.lastCall` in @vitesse/agent-loop/step). A call this loop will never run is
                // dropped, so a reply that is only a call ends the turn with no text. Observed: GPT-5.4 mini
                // read "Answer now" and asked for a tool anyway, and the person saw "The model returned
                // nothing" after two good writes.
                const lastRound = round === MAX_TOOL_ROUNDS - 1;
                if (lastRound) {
                    await this.store.append(this.id, 'assistant', 'That is as many tools as I can run for this question. Answer now with what you have.');
                }
                roundAt = turnParts(assistant).length;
                this.archivedAt = roundAt; // everything before this point is on the record now
                reply = await this.withUpstreamRetry(epoch, async () => chat(await this.store.history(this.id), {
                    model,
                    vision: seesImages(model),
                    effort: this.effort,
                    systemPrompts: standingPrompts(this.opts.experts?.()),
                    tools: lastRound ? undefined : this.scopedTools(),
                    onDelta: this.streamInto(assistant, epoch),
                    onReasoning: this.reasoningInto(assistant, epoch),
                    onToolCall: this.announceTool(assistant, epoch),
                    onToolArgs: this.streamToolArgs(assistant, epoch),
                }));
                if (this.isCurrent(epoch))
                    this.settleRound(assistant, roundAt, reply);
                this.addCost(reply.usage?.costUsd);
                this.addTokens(reply.usage);
            }
            /*
             * STOPPED WHILE WE WERE AWAY.
             *
             * `addCost` above has already recorded what the call spent — that is real and stays. Nothing
             * below is: the text is not written to the turn, not appended to the archive, and the status
             * is not touched, because the person has already moved on and `stop()` set it. Writing the
             * answer here would be the transcript arguing with the button they just pressed.
             *
             * TRUE, not false: the turn WAS taken. The question — or the agent return — was appended and
             * paid for; the person then stopped the answer. A caller holding a queued return must not
             * re-send it because the reader interrupted the reply to it.
             */
            if (!this.isCurrent(epoch))
                return true;
            // Per-turn timing, kept so the header can show what this thread actually costs in latency.
            // TTFT comes from the stream (first token); the total is measured here around the whole call.
            this.timings.push({ ttftMs: reply.ttftMs, totalMs: Date.now() - startedAt });
            // The whole turn's prose, in order — every round's, not the last round's. See TurnPart.
            syncAggregates(assistant);
            /*
             * CHECKED HERE, ONCE, AGAINST WHAT THIS TURN ACTUALLY RAN.
             *
             * `assistant.tools` already holds both phases — the call with its arguments, and the outcome
             * with its data — so the calls can be rebuilt without walking the log. A row still `running`
             * or `unknown` is skipped: an unanswered call says what was asked and nothing about what is
             * true, so it can neither source a claim nor contradict one.
             */
            const ran = (assistant.tools ?? [])
                .filter((t) => t.state === 'ok' || t.state === 'error')
                .map((t) => ({
                name: t.name,
                argumentsJson: t.argumentsJson ?? '',
                output: t.data === undefined ? (t.message ?? '') : `${t.message ?? ''}\n${JSON.stringify(t.data)}`,
            }));
            if (ran.length) {
                const invented = uncitedSources('', reply.text, ran);
                const wrong = misattributed('', reply.text, ran);
                if (invented.length)
                    assistant.uncited = invented;
                if (wrong.length)
                    assistant.misattributed = wrong.map((m) => `${m.value} reported of ${m.claimedOf}, actually ${m.actuallyOf}`);
            }
            assistant.streaming = false;
            assistant.thinking = false;
            // A model that reasoned but never streamed it chunk-by-chunk still reports it on the reply.
            // Taking it here as a fallback means the disclosure works on both kinds of provider, rather
            // than only on the ones that stream reasoning live.
            if (!assistant.reasoning && reply.reasoning)
                assistant.reasoning = reply.reasoning;
            if (!reply.text)
                assistant.error = 'The model returned nothing. Try again, or pick a different model.';
            this.setStatus(reply.text ? 'idle' : 'error');
            // The thinking rides along with the answer it produced, so reopening the thread shows both —
            // THIS round's thinking, not the turn's whole aggregate: the earlier rounds' thoughts are on
            // the tool rows they led to, and writing them here again drew every thought twice on reload.
            if (reply.text) {
                // The turn's own measurements travel WITH it. Held only in memory they vanished on reopen,
                // and the header emptied out until the next message — a transcript showing less on its
                // second read.
                // And who answered it, beside the measurements — what a reopened thread resumes with.
                const seq = await this.store.append(this.id, 'assistant', reply.text, reply.reasoning, reply.reasoningMs, {
                    totalMs: Date.now() - startedAt,
                    ...(reply.ttftMs !== undefined ? { ttftMs: reply.ttftMs } : {}),
                    // The TURN's totals, not the last call's — a turn that ran tools made several billed
                    // calls and the row has to carry all of them, or a reopened thread reports less than the
                    // live one did.
                    ...(this.turnCostUsd !== undefined ? { costUsd: this.turnCostUsd } : {}),
                    ...(this.turnTokens !== undefined ? { tokens: this.turnTokens } : {}),
                    model,
                }, undefined, undefined, undefined, settings);
                if (seq !== undefined)
                    assistant.seq = seq;
            }
            // Chapters are re-read as the thread GROWS, unlike the title, which is settled once.
            //
            // Two thresholds, both deliberate. A short thread has no structure to find — segmenting four
            // messages produces one chapter called roughly what the title already says — so nothing runs
            // below the floor. And each pass is a model call, so re-running it on every turn would put a
            // second billed call behind every message the user sends. Growing by a stride is the signal
            // that the shape may have changed.
            if (reply.text && this.shouldSegment()) {
                this.segmentedAt = this._turns.length;
                void this.opts.autoChapters?.(this.id).catch(() => undefined); // navigation, never a failed turn
            }
            // And the line the person might send next — after the append, so the plane reads this answer.
            if (reply.text)
                void this.suggestNextFor(assistant, epoch);
        }
        catch (e) {
            // A call that fails AFTER a stop is not this thread's problem any more — the person is gone
            // from that turn, and painting an error onto a stopped one would resurrect it. TRUE for the
            // same reason as above: the turn was taken, whatever became of its answer.
            if (!this.isCurrent(epoch))
                return true;
            assistant.streaming = false;
            // Whatever it thought before it failed STAYS — it is often the only account of what went
            // wrong. Only the live flag clears, so the disclosure settles to "Thought for Ns" instead of
            // spinning forever under an error.
            assistant.thinking = false;
            // Prose, always. `e.message` from the edge is already self-describing (the platform's rule for
            // every failure), so it is shown rather than replaced with a generic apology.
            assistant.error = e instanceof Error ? e.message : String(e);
            this.setStatus('error');
        }
        finally {
            // `inFlight` belongs to whichever turn is current. A stale turn clearing it would strand the
            // one that replaced it with no way to be stopped.
            if (this.isCurrent(epoch)) {
                this.inFlight = null;
                this.clearInFlight();
            }
            this.emit();
            // An agent that finished mid-turn queued its answer rather than being refused. Now that the
            // thread is free, hand it over.
            void this.drainReturns();
        }
        return true;
    }
    /** Abandon the turn in flight. The partial reply stays visible — the user heard it. */
    /**
     * Stop, and mean it.
     *
     * The transport takes no abort signal, so the call keeps running and keeps billing — the org pays
     * for the fraction of a cent already committed, which is the honest outcome and the one worth
     * having. What changes is that NOTHING it produces from here is shown or stored: bumping the
     * epoch makes every closure that turn handed out stale in the same tick, so the next delta is
     * dropped instead of appended. Before this, Stop cleared the spinner and the text kept arriving.
     *
     * What it costs is still recorded when the call resolves. Usage is not something a person can
     * decline after the fact by pressing a button.
     */
    /**
     * `by` says who is stopping. A person pressing Stop is done with this answer, so the resume note
     * goes too. The page going away (`pagehide`) is not a decision about the answer — a reload is the
     * common case — so the note stays, and the same tab picks the turn up when it comes back.
     */
    stop(by = 'user') {
        this.turnEpoch++;
        this.inFlight?.abort();
        this.inFlight = null;
        if (by === 'user')
            this.clearInFlight();
        /*
         * AND WHAT THE TURN WAS WAITING ON A PERSON FOR, closed with it.
         *
         * A question the turn asked (`ask_user`) or a write it asked to have approved stayed on screen
         * after "stopped", and answering it later handed the stopped turn an answer and set it going
         * again. Both are keyed by the turn's run id, so a question an agent this turn started is
         * asking stays: a stop does not reach what the turn began.
         */
        if (this.currentRunId) {
            cancelAsk(this.currentRunId);
            this.opts.onStopped?.(this.currentRunId);
        }
        const last = this._turns[this._turns.length - 1];
        if (last?.streaming)
            last.streaming = false;
        if (last)
            last.thinking = false;
        /*
         * A CALL STILL IN FLIGHT IS CANCELLED, ON SCREEN AND ON THE RECORD.
         *
         * The rows kept their spinner after Stop as if the work went on, and on reload they were gone:
         * the dispatch was archived, the outcome never was, and the fold drew nothing for a call with no
         * answer. A person who stopped a turn should see what was cut short, then and later. Any real
         * outcome that lands after this is dropped by the epoch check, so the record stays "stopped".
         */
        for (const row of last?.tools ?? []) {
            if (row.state !== 'running')
                continue;
            row.state = 'error';
            row.message = STOPPED_TOOL_MESSAGE;
            void this.store.appendToolResult?.(this.id, row.id, { ok: false, message: STOPPED_TOOL_MESSAGE }).catch(() => undefined);
        }
        this.setStatus('idle');
        /*
         * WHAT WAS ALREADY WRITTEN IS KEPT.
         *
         * Stopping is not undoing. If the model has produced most of a document and you stop it there,
         * that text is the reason you stopped — you have what you needed — and throwing it away on
         * reload would make Stop the most destructive button on the surface. So the partial answer is
         * appended to the archive exactly as a finished one would be, with the thinking that produced
         * it, and reopening the thread shows it.
         *
         * `stopped` marks it as cut short rather than complete, because a reader coming back to a
         * paragraph that ends mid-sentence should be told which of those it is.
         *
         * Fire-and-forget: `stop()` is called from a click handler and must return immediately. A write
         * that fails leaves the turn on screen and out of the archive, which is the same outcome as
         * before and is surfaced by the store's own error path.
         */
        if (last && last.role === 'assistant' && last.text) {
            last.stopped = true;
            // ONLY THE UNARCHIVED TAIL — see `archivedAt`. A tool round's prose is already on the record
            // beside its calls; writing the whole turn again drew that paragraph twice after a reload.
            const tail = (last.parts ?? []).slice(this.archivedAt);
            const text = joinProse(tail.filter((p) => p.kind === 'text').map((p) => p.text));
            const thought = tail.filter((p) => p.kind === 'reasoning');
            const reasoning = thought.map((p) => p.text).filter(Boolean).join('\n\n') || undefined;
            const reasoningMs = thought.reduce((a, p) => a + (p.ms ?? 0), 0) || undefined;
            // Marked as cut short IN THE RECORD, so a reopened thread still says "stopped here" rather
            // than presenting half an answer as the whole of one. Nothing new said → nothing to write:
            // the round already on the record, and its unresolved call, tell the story.
            if (text)
                void this.store
                    .append(this.id, 'assistant', text, reasoning, reasoningMs, { totalMs: Date.now() - this.turnStartedAt, stopped: true, ...(this.turnSettings?.model ? { model: this.turnSettings.model } : {}) }, undefined, undefined, undefined, this.turnSettings)
                    .then((seq) => {
                    if (seq !== undefined)
                        last.seq = seq;
                    this.emit();
                })
                    .catch(() => undefined);
        }
    }
    /**
     * The thread is being deleted: write nothing more to it, from any path.
     *
     * Dropping the reference was not enough. This object kept polling its agent trees, and the
     * delete itself stops those agents, so within one poll `harvest` found a stopped agent and sent
     * its return as a new turn. That turn wrote the deleted thread back into the archive, and then
     * ran the model three more times in it.
     *
     * NOT `stop()`, because `stop()` writes: the partial answer and the "stopped" tool results go to
     * the archive, fire-and-forget, and can land after the delete. Here the turn is only cut off and
     * the waits on the person are closed. The delete removes what was already written.
     */
    retire() {
        this.retired = true;
        this.turnEpoch++;
        this.inFlight?.abort();
        this.inFlight = null;
        this.clearInFlight();
        if (this.currentRunId) {
            cancelAsk(this.currentRunId);
            this.opts.onStopped?.(this.currentRunId);
        }
        for (const tree of this.trees.values())
            tree.stop();
        this.returns.length = 0;
        this.setStatus('idle');
    }
    /**
     * ONE QUIET RETRY ON A PROVIDER BLIP.
     *
     * A 502 from the model provider surfaced in the thread as "the model provider is having trouble"
     * on the first hiccup, while the person sat there — and a second attempt a moment later almost
     * always works. So a failure that reads as upstream (a 5xx, a gateway, the provider itself) is
     * tried once more after a short pause. Once: a provider that is really down should say so, not
     * be hammered. Nothing else is retried — a refused request or a bad argument is the same the
     * second time.
     */
    async withUpstreamRetry(epoch, call) {
        try {
            return await call();
        }
        catch (e) {
            const raw = e instanceof Error ? e.message : String(e);
            const upstream = /\b5\d\d\b/.test(raw) && /(unavailable|error|gateway|upstream|provider|status|overloaded)/i.test(raw);
            if (!upstream || !this.isCurrent(epoch))
                throw e;
            await new Promise((r) => setTimeout(r, UPSTREAM_RETRY_MS));
            if (!this.isCurrent(epoch))
                throw e;
            return await call();
        }
    }
    push(t) {
        this._turns.push(t);
        this.emit();
    }
    setStatus(s) {
        const was = this.busy;
        this._status = s;
        if (this.busy !== was)
            this.watchTurn(this.busy);
        this.emit();
    }
    /**
     * Start or stop the silence watchdog with the turn.
     *
     * An interval rather than a timer re-armed on every delta: `emit` runs once per streamed chunk, so
     * a clear-and-set there would be thousands of timer operations per turn to answer a question that
     * only needs asking twice a minute.
     */
    watchTurn(on) {
        if (this.watch)
            clearInterval(this.watch);
        this.watch = null;
        if (!on)
            return;
        this.lastSignal = Date.now();
        this.watch = setInterval(() => {
            if (!this.busy)
                return this.watchTurn(false);
            if (Date.now() - this.lastSignal < TURN_SILENCE_MS)
                return;
            this.abandonTurn();
        }, TURN_WATCH_MS);
    }
    /**
     * End a turn that has gone silent, and say so.
     *
     * `stop` already does everything that has to happen — bump the epoch so anything still arriving is
     * dropped, mark the running tool rows, archive the partial answer — and the only difference here
     * is WHO ended it. A person pressing Stop knows why the answer is short; a reader coming back to a
     * thread that quietly gave up does not, so the turn carries the reason.
     */
    abandonTurn() {
        this.stop();
        const last = this._turns[this._turns.length - 1];
        if (last && last.role === 'assistant' && !last.error)
            last.error = HUNG_TURN_MESSAGE;
        this.setStatus('error');
    }
    /**
     * The per-chunk sink for ONE model call: clears the turn's visible text and returns the hook that
     * appends to it, repainting as each chunk lands.
     *
     * It starts each round from what the turn has ALREADY SAID rather than from nothing.
     *
     * A turn can span several model calls — the tool loop asks, runs a tool, then asks again — and
     * this used to clear the text at the top of every round, on the reasoning that only the last
     * reply is the turn's value. It is not: a model that writes a sentence, calls a tool, and then
     * continues has said both sentences, and the archive holds both as separate assistant messages.
     * Clearing meant the reader watched a paragraph arrive and then be silently replaced by a
     * different one a second later — reported exactly that way, and read as the model changing its
     * mind about what it had just told them.
     *
     * So each round appends after the last, and the assignment that closes the turn carries the same
     * prefix. What is on screen mid-turn is then what the turn is about to become, which is the
     * property the reset was reaching for and the wrong way round.
     *
     * No throttle. Lit already coalesces the repaints it schedules within a tick, and `emit` is a
     * counter bump rather than a transcript copy, so a chunk costs approximately nothing — while a
     * throttle would flatten precisely the character-by-character arrival this exists to show.
     */
    /**
     * The live tree for one root, created on first sight and kept.
     *
     * Owned HERE rather than by the view, because a `RunTree` does IO and a view must not (the
     * Cardinal Rule) — and because a tree outlives any one render: the transcript can scroll a spawn
     * off screen and back without restarting its poll or losing what it knows.
     */
    tree(rootRunId) {
        return this.trees.get(rootRunId);
    }
    /**
     * The run this thread spawned with this id, at any depth — or undefined when it is not ours.
     *
     * Asked by the panel about a pending question: a specialist's `ask_user` carries its own run, and
     * the thread that started it is where the person is (see `askShowsIn`). Read from the trees this
     * thread already follows, so a run is ours exactly when it appears in one of them — no second
     * record of who spawned whom.
     */
    spawnedRun(runId) {
        for (const tree of this.trees.values()) {
            const run = tree.run(runId);
            if (run)
                return run;
        }
        return undefined;
    }
    /**
     * Notice that a tool started an agent, and begin following it.
     *
     * Recognised by the structured reply rather than by parsing prose: `SpawnSubagent` returns the
     * ids, so a spawn is identified by the shape of what came back. Matching on the message text would
     * break the first time the sentence was reworded.
     *
     * STARTING AN AGENT IS NOT ONLY `spawn_subagent`. Waking a FINISHED one with `message_agent`
     * mints a continuation, which is a run like any other and — when its predecessor hung off a chat
     * turn — a ROOT like any other. Keying on the shape rather than on the tool name is what lets
     * that arrive here for free: the wake now answers with `root_run_id` too, and this follows it
     * without knowing which verb produced it. It was the absence of the FIELD, never a test on the
     * name, that left a woken agent unfollowed and its answer stranded in its own thread.
     */
    noteSpawn(turn, outcome) {
        const root = outcome.data?.rootRunId;
        if (!outcome.ok || !root || this.trees.has(root))
            return;
        turn.spawnedRoots = [...(turn.spawnedRoots ?? []), root];
        this.followSpawned(root);
    }
    /**
     * Follow a tree and carry it forward — the ONE path, used live and on resume.
     *
     * Shared deliberately. The live case and the reopened case did different things, which is how a
     * resumed conversation ended up showing a spawn with no tree under it and no way into the agent's
     * chat. One function means the two cannot drift again: whatever a fresh spawn does, a reopened one
     * does too.
     */
    followSpawned(rootRunId) {
        if (this.trees.has(rootRunId))
            return;
        const tree = new RunTree();
        // The conversation re-emits the tree's changes as its own, so a view subscribed to the
        // conversation repaints as agents advance without subscribing to each tree separately.
        tree.addEventListener('change', () => {
            this.emit();
            // Harvest on the tree's own signal rather than only when THIS tab's driver returns: a tree
            // adopted by another tab still finishes, and its answer still belongs in this thread.
            this.harvest(rootRunId);
        });
        tree.watch(rootRunId);
        this.trees.set(rootRunId, tree);
        this.emit();
        this.drive(rootRunId);
    }
    /**
     * Whether the thread has grown enough to be worth re-segmenting.
     *
     * Separate from the call site so the cadence can be asserted directly. `segmentedAt` starts at
     * zero and is set to the turn count at each pass, so a conversation reopened mid-life segments
     * once on its next reply and then falls back into the stride — which is right: the archive's
     * chapters are already there, and this is only about keeping up with what is added.
     */
    shouldSegment() {
        const n = this._turns.length;
        return n >= CHAPTER_FLOOR_TURNS && n - this.segmentedAt >= CHAPTER_STRIDE_TURNS;
    }
    /**
     * Bring a finished agent's answer back into THIS conversation.
     *
     * WHY IT HAS TO EXIST. Every other edge of the return path was already built: a child run returns
     * to its parent run along the spawn edge, and the parent folds it into its own answer — that is
     * what `AgentReturn` is for, and it is recursive by construction, so a tree three deep resolves
     * upward without anyone writing a third case. The main chat was the one place the chain stopped,
     * because the main chat is not a run. Nothing carried the ROOT's answer across that boundary, so
     * agents finished, wrote their conclusions into their own transcripts, and the person who asked
     * the question was told they would "report back" and then never heard anything again.
     *
     * The return arrives in the user slot because that is the slot the model reads, and an answer the
     * model cannot read is not a return. It is flagged `returned` so it is never DRAWN as the user's
     * words, and the prose says where it came from, so the provenance survives into the archive rather
     * than living only in a UI flag.
     *
     * ONCE PER ROOT. `reported` is keyed by root run id and is checked before anything is sent — this
     * is called from a poll that fires every two seconds, and a report-back that ran per tick would
     * turn one finished agent into an unbounded conversation with itself.
     */
    harvest(rootRunId) {
        // Not into a deleted thread. `send` refuses it as well, but a refused return stays queued, and
        // the drain tries it again each time the thread is idle, without end.
        if (this.retired || this.reported.has(rootRunId))
            return;
        /*
         * OVER ATTEMPTS AS WELL AS RUNS, because a continuation is folded onto the run it continues.
         *
         * A woken agent gets a NEW run id, and `RunTree.index` files it as an ATTEMPT of its origin
         * rather than as a root of its own. So following the woken id produced a tree whose only root
         * was the ORIGIN, and this lookup — matching `run.runId` alone — returned undefined on every
         * tick, forever. The tab followed the tree, drove it, watched it finish, and harvested nothing.
         *
         * Read through `latestAttempt` for the same reason: the origin's row is the ended earlier
         * attempt, and reporting ITS ending would tell the person the agent stopped when in fact it was
         * woken and answered. The newest attempt is the run that produced the answer.
         */
        const node = this.trees
            .get(rootRunId)
            ?.roots.find((n) => n.run.runId === rootRunId || n.attempts.some((a) => a.runId === rootRunId));
        if (!node)
            return;
        const run = latestAttempt(node);
        if (run.status !== RUN_FINISHED)
            return;
        this.reported.add(rootRunId);
        // With what the person stopped beneath it — the same line the DO writes, from the same tree.
        const everyRun = (n) => [n.run, ...n.attempts, ...n.children.flatMap(everyRun)];
        this.returns.push({ text: returnProse(run, personStoppedBelow(everyRun(node), run.runId)), rootRunId });
        void this.drainReturns();
    }
    /**
     * Hand queued returns to the model, one turn at a time.
     *
     * Serialised because two agents finishing a second apart would otherwise both try to take a turn,
     * and `send` refuses the second — which would silently drop a return rather than delay it. Waiting
     * for idle costs a few seconds and loses nothing.
     *
     * THE ITEM DOES NOT LEAVE THE QUEUE UNTIL IT HAS BEEN SAID, and that is the whole correctness
     * argument.
     *
     * It used to `shift()` first and then call `send`, which refuses — silently, returning void —
     * whenever the thread is busy. The `busy` check at the top of this function is not enough to
     * prevent that: it is read ONCE, before the loop, and a turn can start between then and any
     * later iteration (a person typing, or the previous return's own turn on the second pass). When
     * it did, the shifted item was already off the queue, nothing put it back, and `reported` had
     * marked its root delivered forever — so the poll would never re-queue it either.
     *
     * That is a finished agent's answer being DESTROYED, and it is what "at the end of its output it
     * all gets deleted, like I never sent that message and it never returned" describes. It is also
     * why a parent stops: the return that would have re-invoked it is the thing that was dropped.
     *
     * So: peek, send, and remove ONLY on a `true`. A refusal leaves the queue exactly as it was and
     * ends the pass — the in-flight turn's own `finally` calls back here when the thread is free.
     */
    async drainReturns() {
        if (this.draining || !this.returns.length)
            return;
        // NOT ON BEHALF OF AN AGENT THAT HAS STOPPED. The queue is left intact rather than dropped: the
        // answers are real and a resumed run should still get them. See `runFinished`.
        if (this.runFinished)
            return;
        if (this.busy)
            return; // a turn is in flight; its `finally` calls back here
        this.draining = true;
        try {
            while (this.returns.length) {
                const next = this.returns[0];
                /*
                 * AN UNSAYABLE ITEM IS DROPPED BEFORE IT IS TRIED, not diagnosed afterwards.
                 *
                 * `send` refuses empty text as well as a busy thread, and the two need opposite responses:
                 * one can never become sayable, the other will be in a moment. Classifying that from the
                 * ITEM is local and always right; classifying it from `busy` after the fact is reading
                 * ambient state to explain a decision somebody else made, and it is wrong the instant the
                 * thread goes idle between the refusal and the check.
                 *
                 * Dropping it is what stops the whole queue parking behind something nothing will ever
                 * accept — and it is also what makes the retry below terminate, because every pass either
                 * waits on a live turn or removes an item.
                 */
                if (!next.text.trim()) {
                    this.returns.shift();
                    continue;
                }
                const took = await this.send(next.text, 'agent', next.rootRunId);
                // Refused ⇒ the thread is busy, which is the only reason left. Leave it exactly where it is
                // and stop; the in-flight turn's own `finally` calls back here.
                if (!took)
                    break;
                this.returns.shift();
            }
        }
        finally {
            this.draining = false;
            /*
             * ONE MORE PASS IF SOMETHING IS STILL WAITING AND NOTHING IS IN FLIGHT.
             *
             * The hand-off relies on the in-flight turn's `finally` calling back — and that call is
             * REFUSED while `draining` is still true. If a turn happened to finish inside our own drain,
             * its callback bounced off that guard and this queue would sit full with nobody scheduled to
             * empty it. Re-checking here closes that window, and it cannot spin: it only re-enters when a
             * turn is genuinely absent, and every refusal above either breaks on `busy` or removes an
             * item, so the queue strictly shrinks.
             */
            if (this.returns.length && !this.busy)
                void this.drainReturns();
        }
    }
    /**
     * Carry a spawned tree forward — THIS TAB is the driver.
     *
     * A spawn creates a run and returns; something has to step it. The tab does, and that is the same
     * answer `resumeAbandonedWork` gives for work found stalled on open: showing up is the clearest
     * signal that the user wants their work advanced, and a server that adopted trees on its own would
     * resume spending while nobody is watching.
     *
     * NOT AWAITED, deliberately. The spawning turn must finish and hand the user their answer — an
     * agent working in the background is the entire point, and blocking the reply on it would make a
     * parallel tool serial. So this is fired and left to run, and progress reaches the screen through
     * the tree's own poll rather than through this promise.
     *
     * ONE DRIVER PER TREE. `driving` guards re-entry: a second driver would claim the lease, bump the
     * epoch and fence out the first mid-step, which presents as a run that randomly stops.
     *
     * When the tab closes mid-run the lease simply expires, the tree becomes adoptable, and the next
     * open finds it through `resumeAbandonedWork` — which is what that function has always been for
     * and has never had anything to pick up until now.
     */
    drive(rootRunId) {
        if (this.driving.has(rootRunId))
            return;
        this.driving.add(rootRunId);
        const step = browserStep(this.opts, this.store);
        void driveToCompletion({
            rootRunId,
            step,
            budgetUsd: TAB_DRIVE_BUDGET_USD,
            /*
             * AN ENDING IS DRAWN AT ONCE, not on the next poll.
             *
             * The tree re-reads every two seconds, which is right for progress and wrong for a stop: a
             * run that hit its step cap kept looking alive for up to a full interval, and the sentence
             * saying so reached the thread a poll after THAT — because `harvest` only fires on a tree
             * change. So "it stopped" arrived several seconds after it stopped, which reads as a stop
             * that did not take.
             *
             * The driver knows at the write. One read, fired then, and the harvest that follows it is on
             * the same tick — so the step cap, the budget, an abort and an ordinary completion all show
             * up the moment they are true.
             */
            onRunTerminal: () => void this.trees.get(rootRunId)?.refresh(),
        })
            .then((passes) => {
            /*
             * A REFUSAL IS NOT A FAILURE, and the difference matters on screen.
             *
             * `fenced` means the tree stopped being this tab's — the user pressed stop, or another tab
             * took it over. Re-claiming would be this tab arguing with the person using it, and saying
             * "error" would be reporting their own decision back to them as a fault. Not claiming at all
             * is the same story: somebody else is already driving it.
             */
            const last = passes[passes.length - 1];
            if (last && !last.claimed && last.refusedBecause && !last.fenced) {
                this.driveError = last.refusedBecause;
                this.emit();
            }
        })
            .catch((e) => {
            // Prose, and surfaced. A driver that dies quietly leaves a tree sitting at QUEUED forever,
            // which reads as an agent that never started rather than as one whose driver fell over.
            this.driveError = e instanceof Error ? e.message : String(e);
            this.emit();
        })
            .finally(() => {
            this.driving.delete(rootRunId);
            // One more read, so the final state lands even if the tree's own poll stopped when the last
            // run went terminal between ticks.
            void this.trees.get(rootRunId)?.refresh();
        });
    }
    /** Add one call's cost, if the provider reported one. Absent input leaves the total absent —
     *  a thread where nothing reported a price shows no figure rather than a confident zero. */
    addCost(usd) {
        if (usd === undefined)
            return;
        this._costUsd = (this._costUsd ?? 0) + usd;
        this.turnCostUsd = (this.turnCostUsd ?? 0) + usd;
        this.emit();
    }
    /** Accumulate a call's tokens. Per CALL, like cost — a turn that runs three tools is four calls. */
    addTokens(usage) {
        const n = (usage?.inputTokens ?? 0) + (usage?.outputTokens ?? 0);
        if (!n)
            return;
        this._tokens = (this._tokens ?? 0) + n;
        this.turnTokens = (this.turnTokens ?? 0) + n;
        this.emit();
    }
    streamInto(turn, epoch) {
        // This closure IS the round: its first delta opens the round's text part, after whatever the
        // turn already holds — a tool call, an earlier paragraph — so the order on screen is the order
        // it happened. Nothing is cleared: what a turn said before it called a tool is part of the turn.
        let part;
        return (delta) => {
            // Stopped. The call is still running on the wire (and still billing), but nothing it produces
            // from here reaches the screen or the archive — which is what Stop means to a person.
            if (!this.isCurrent(epoch))
                return;
            // The answer starting is the thinking ending. Clearing the flag here rather than waiting for
            // the reply keeps the disclosure honest during the gap: a model that thinks for eight seconds
            // and then writes for four should not read "Thinking" through the writing.
            turn.thinking = false;
            this.closeThinking(turn);
            if (!part) {
                part = { kind: 'text', text: '' };
                turnParts(turn).push(part);
            }
            part.text += delta;
            syncAggregates(turn);
            this.emit();
        };
    }
    /** A thinking part stops being live the moment anything follows it. */
    closeThinking(turn) {
        for (const p of turn.parts ?? [])
            if (p.kind === 'reasoning' && p.live)
                p.live = false;
    }
    /**
     * SETTLE THE ROUND against the finished reply.
     *
     * The stream is what the reader watched; the reply is what the provider says was produced, and it
     * wins. A provider that did not stream — no deltas at all — still produced text and thinking, so
     * the round's parts are created here if the stream never opened them; one that did stream has its
     * parts corrected to the final text, which is the same text on every provider that behaves.
     */
    settleRound(turn, roundAt, reply) {
        const parts = turnParts(turn);
        const round = parts.slice(roundAt);
        const thought = round.find((p) => p.kind === 'reasoning');
        if (thought) {
            thought.live = false;
            if (reply.reasoningMs !== undefined)
                thought.ms = reply.reasoningMs;
        }
        else if (reply.reasoning) {
            // Never streamed, reported whole. It goes BEFORE anything else this round produced.
            parts.splice(roundAt, 0, { kind: 'reasoning', text: reply.reasoning, ...(reply.reasoningMs !== undefined ? { ms: reply.reasoningMs } : {}) });
        }
        const said = [...parts.slice(roundAt)].reverse().find((p) => p.kind === 'text');
        if (said)
            said.text = reply.text;
        else if (reply.text)
            parts.push({ kind: 'text', text: reply.text });
        syncAggregates(turn);
    }
    /**
     * A TOOL ROW THE MOMENT THE MODEL NAMES IT — not when the model call returns.
     *
     * A step that asks for five tools used to be silent for the whole call and then put five rows up
     * at once, which reads as a stall followed by a dump. The model settles on the first tool seconds
     * into the call; the stream says so, and this is what puts that on screen.
     *
     * The row is deliberately incomplete: a name and nothing else, because the arguments are still
     * arriving. Phase 1 below fills them in on the same row rather than adding a second one.
     */
    announceTool(turn, epoch) {
        return (call) => {
            if (!this.isCurrent(epoch))
                return;
            // A retry or a re-drain can announce the same id twice; the row is the same row.
            if (turn.tools?.some((t) => t.id === call.id))
                return;
            // Deciding to call a tool ends the thinking that led to it, same as the first answer token.
            turn.thinking = false;
            turn.tools = [...(turn.tools ?? []), { id: call.id, name: call.name, argumentsJson: '', state: 'running' }];
            this.closeThinking(turn);
            turnParts(turn).push({ kind: 'tool', id: call.id });
            this._running = [...this._running, call.name];
            this.setStatus('working');
            this.emit();
        };
    }
    /**
     * THE ARGUMENTS, ARRIVING.
     *
     * For most tools this is noise — a path, an id, a query — and the row shows the name. For the
     * tools whose argument IS the deliverable, most of all `models.write`, it is the work itself: an
     * SCXML document being written, which the reader can watch instead of waiting for a sentence
     * saying it happened. The row carries whatever has arrived; what draws it decides whether that is
     * worth showing.
     */
    streamToolArgs(turn, epoch) {
        return (callId, delta) => {
            if (!this.isCurrent(epoch))
                return;
            const row = turn.tools?.find((t) => t.id === callId);
            // No row yet means no START was seen for this id — malformed, and the reply will still carry
            // the finished arguments, so dropping the fragment loses nothing.
            if (!row)
                return;
            row.argumentsJson = (row.argumentsJson ?? '') + delta;
            this.emit();
        };
    }
    /**
     * The per-chunk sink for REASONING, and deliberately the opposite of `streamInto`: it APPENDS
     * across the tool loop rather than resetting each round.
     *
     * `text` resets because only the final round's prose is the answer. Thinking is not like that —
     * every round's reasoning is work this turn did, and the earliest round is usually where the model
     * decided which tools to call. Resetting would show that, then erase it the moment a tool
     * returned, which is precisely the vanishing the disclosure exists to stop.
     */
    reasoningInto(turn, epoch) {
        // One sink per model call, so this closure IS the round. Its first delta is where a new round's
        // thinking begins, and separating rounds there keeps two unrelated trains of thought — one that
        // chose a tool, one that read its result — from running together as a single paragraph.
        let part;
        return (delta) => {
            if (!this.isCurrent(epoch))
                return; // stopped — see streamInto
            turn.thinking = true;
            if (!part) {
                part = { kind: 'reasoning', text: '', live: true };
                turnParts(turn).push(part);
            }
            part.text += delta;
            syncAggregates(turn);
            this.emit();
        };
    }
    emit() {
        // A SIGN OF LIFE. Everything a turn produces passes through here, so this is where the silence
        // watchdog takes its reading — see TURN_SILENCE_MS.
        this.lastSignal = Date.now();
        // COUNTER, NOT A COPY.
        //
        // This used to reallocate the whole transcript on every emit so a reference-diffing renderer
        // would notice in-place streaming writes. That is O(n) per emit and roughly four emits per turn,
        // so a long conversation costs O(n^2) — measurable well before a session feels long. A monotonic
        // revision gives the renderer the same signal in constant time.
        this._revision++;
        this.dispatchEvent(new CustomEvent('change'));
    }
}
//# sourceMappingURL=conversation.js.map